import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useBreadcrumbExtra } from '../components/layout';
import { QRCodeSVG } from 'qrcode.react';
import MetaSetupGuide from '../components/settings/MetaSetupGuide';
import BYODBPanel from '../components/settings/BYODBPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple Modal component
function Modal({ isOpen, onClose, title, children }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h3 className="text-lg font-bold">{title}</h3>
          <button onClick={onClose} className="text-2xl text-gray-500 hover:text-gray-700 leading-none">&times;</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

// Toast component
function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-4 right-4 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium ${type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
      {message}
    </div>
  );
}

// API helper
const api = {
  get: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return await res.json();
    } catch (err) {
      console.error('API error:', err);
      return {};
    }
  },
  post: async (endpoint, data, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (err) {
      console.error('API error:', err);
      return { error: err.message };
    }
  },
  put: async (endpoint, data, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (err) {
      console.error('API error:', err);
      return { error: err.message };
    }
  },
  delete: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      return await res.json();
    } catch (err) {
      console.error('API error:', err);
      return { error: err.message };
    }
  }
};

function SettingsPage({ token, user }) {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'branding', 'homepage', 'integrations', 'plan'
  const [integrations, setIntegrations] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showTwilioForm, setShowTwilioForm] = useState(false);
  const [showRazorpayForm, setShowRazorpayForm] = useState(false);
  const [colorPresets, setColorPresets] = useState([]);
  
  // SMTP state
  const [smtpConfig, setSmtpConfig] = useState(null);
  const [showSmtpForm, setShowSmtpForm] = useState(false);
  const [smtpForm, setSmtpForm] = useState({ smtp_host: 'smtpout.secureserver.net', smtp_port: 465, smtp_email: '', smtp_password: '', from_name: 'शिक्षा मित्र', use_ssl: true });
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  
  // WhatsApp Business state
  const [whatsappConfig, setWhatsappConfig] = useState(null);
  const [showWhatsappForm, setShowWhatsappForm] = useState(false);
  const [whatsappForm, setWhatsappForm] = useState({ phone_number_id: '', access_token: '', business_account_id: '', verify_token: '' });
  
  // Social Media Credentials state
  const [socialMediaConfigs, setSocialMediaConfigs] = useState([]);
  const [showSocialForm, setShowSocialForm] = useState(false);
  const [socialPlatform, setSocialPlatform] = useState('facebook');
  const [socialForm, setSocialForm] = useState({ api_key: '', api_secret: '', access_token: '', page_id: '' });

  // Meta Connection (Facebook Ads + WhatsApp) — school's OWN account
  const [metaSaving, setMetaSaving] = useState(false);
  const [showMetaGuide, setShowMetaGuide] = useState(false);
  const [metaForm, setMetaForm] = useState({
    fb_ad_account_id: '', fb_page_id: '', fb_access_token: '',
    wa_phone_number_id: '', wa_access_token: '', wa_business_account_id: ''
  });

  // Gemini API Key state
  const [geminiKeyStatus, setGeminiKeyStatus] = useState({ has_key: false, key_preview: null });
  const [showGeminiForm, setShowGeminiForm] = useState(false);
  const [geminiKeyInput, setGeminiKeyInput] = useState('');
  const [geminiSaving, setGeminiSaving] = useState(false);
  const [geminiTesting, setGeminiTesting] = useState(false);
  const [geminiTestResult, setGeminiTestResult] = useState(null);
  const [showGeminiQR, setShowGeminiQR] = useState(false);

  // Connection History state
  const [connectionLogs, setConnectionLogs] = useState([]);
  const [emailStats, setEmailStats] = useState({ total_sent: 0, total_newsletters: 0 });
  const [historyFilter, setHistoryFilter] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Plan & Usage state
  const [planInfo, setPlanInfo] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [planComparison, setPlanComparison] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedNewPlan, setSelectedNewPlan] = useState(null);
  const [changingPlan, setChangingPlan] = useState(false);
  
  const [twilioForm, setTwilioForm] = useState({ account_sid: '', auth_token: '', whatsapp_number: '' });
  const [razorpayForm, setRazorpayForm] = useState({ key_id: '', key_secret: '' });
  
  // Profile form state
  const [basicInfo, setBasicInfo] = useState({});
  const [contactInfo, setContactInfo] = useState({});
  const [socialLinks, setSocialLinks] = useState({});
  const [branding, setBranding] = useState({});
  const [homeSections, setHomeSections] = useState([]);
  const [schoolSubdomain, setSchoolSubdomain] = useState(null);
  
  // Team & CTA state
  const [teamMembers, setTeamMembers] = useState([]);
  const [callToActions, setCallToActions] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryVideos, setGalleryVideos] = useState([]);
  const [galleryAlbums, setGalleryAlbums] = useState([]);
  const [galleryCategories, setGalleryCategories] = useState({});
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [teamForm, setTeamForm] = useState({ name: '', name_hindi: '', role: '', role_hindi: '', qualification: '', experience: '', phone: '', email: '', order: 0 });
  
  // Enhanced Gallery state
  const [gallerySubTab, setGallerySubTab] = useState('photos');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showAlbumModal, setShowAlbumModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [photoForm, setPhotoForm] = useState({});
  const [videoForm, setVideoForm] = useState({});
  const [albumForm, setAlbumForm] = useState({});

  const schoolId = user?.school_id;
  const isSchoolAdmin = user?.role === 'school_admin';

  // Append school name to breadcrumb when known (e.g., Home › Settings › ABC Public School)
  const dynamicSchoolName = (language === 'hi' && basicInfo?.name_hindi) ? basicInfo.name_hindi : basicInfo?.name;
  useBreadcrumbExtra(dynamicSchoolName || null);
  const isPlatformAdmin = user?.role === 'platform_admin';

  useEffect(() => { 
    loadData();
    loadPlanData();
    loadPlatformSettings();
  }, []);

  const loadPlanData = async () => {
    try {
      const [compareRes, pricingRes, myPlanRes] = await Promise.all([
        api.get('/api/plans/compare', token),
        api.get('/api/plans/pricing', token),
        schoolId ? api.get(`/api/plans/my-plan?school_id=${schoolId}`, token) : Promise.resolve({})
      ]);
      setPlanComparison(compareRes?.comparison);
      setPlanInfo(pricingRes);
      setCurrentPlan(myPlanRes?.plan || { plan_name: user?.school_plan || 'standard' });
      
      // Load usage stats if school_id exists
      if (user?.school_id) {
        const usageRes = await api.get(`/api/ai-usage/stats/school/${user.school_id}?days=30`, token);
        setUsageStats(usageRes);
      }
    } catch (err) {
      console.error('Error loading plan data:', err);
    }
  };

  const loadPlatformSettings = async () => {
    try {
      const [smtpRes, waRes, socialRes] = await Promise.all([
        api.get('/api/platform-settings/smtp', token),
        api.get('/api/platform-settings/whatsapp', token),
        api.get('/api/platform-settings/social-media', token),
      ]);
      if (smtpRes?.success) setSmtpConfig(smtpRes);
      if (waRes?.success) setWhatsappConfig(waRes);
      if (socialRes?.success) setSocialMediaConfigs(socialRes.configs || []);

      // Load Gemini API key status (school_admin or platform_admin with school_id)
      if (schoolId) {
        const gemRes = await api.get(`/api/schools/${schoolId}/gemini-key`, token);
        if (gemRes && typeof gemRes.has_key === 'boolean') {
          setGeminiKeyStatus({ has_key: gemRes.has_key, key_preview: gemRes.key_preview });
        }
      }
    } catch (err) {
      console.error('Error loading platform settings:', err);
    }
  };

  // Gemini API Key functions
  const saveGeminiKey = async () => {
    if (!geminiKeyInput || geminiKeyInput.trim().length < 10) {
      setToast({ message: language === 'hi' ? 'वैध Gemini API Key डालें' : 'Enter a valid Gemini API Key', type: 'error' });
      return;
    }
    if (!schoolId) {
      setToast({ message: language === 'hi' ? 'School ID उपलब्ध नहीं है' : 'School ID not available', type: 'error' });
      return;
    }
    setGeminiSaving(true);
    setGeminiTestResult(null);
    try {
      const res = await api.put(`/api/schools/${schoolId}/gemini-key`, { gemini_api_key: geminiKeyInput.trim() }, token);
      if (res?.success) {
        setToast({ message: language === 'hi' ? 'Gemini Key verify होकर save हो गई!' : 'Gemini Key verified & saved!', type: 'success' });
        setShowGeminiForm(false);
        setGeminiKeyInput('');
        loadPlatformSettings();
      } else {
        setToast({ message: res?.detail || (language === 'hi' ? 'Save failed — key invalid हो सकती है' : 'Save failed — key may be invalid'), type: 'error' });
      }
    } catch (err) {
      setToast({ message: 'Error: ' + err.message, type: 'error' });
    }
    setGeminiSaving(false);
  };

  const testGeminiKey = async () => {
    if (!schoolId) return;
    setGeminiTesting(true);
    setGeminiTestResult(null);
    try {
      const res = await api.post(`/api/schools/${schoolId}/gemini-key/test`, {}, token);
      setGeminiTestResult(res);
      setToast({ message: res.message, type: res.success ? 'success' : 'error' });
    } catch (err) {
      setGeminiTestResult({ success: false, message: 'Test failed: ' + err.message });
    }
    setGeminiTesting(false);
  };

  const removeGeminiKey = async () => {
    if (!schoolId) return;
    if (!window.confirm(language === 'hi' ? 'Gemini API Key हटाना चाहते हैं?' : 'Remove Gemini API Key?')) return;
    try {
      const res = await fetch(`${API_URL}/api/schools/${schoolId}/gemini-key`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) {
        setToast({ message: language === 'hi' ? 'Key हटा दी गई' : 'Key removed', type: 'success' });
        setGeminiTestResult(null);
        loadPlatformSettings();
      }
    } catch (err) {
      setToast({ message: 'Remove failed', type: 'error' });
    }
  };

  // SMTP functions
  const saveSmtp = async () => {
    if (!smtpForm.smtp_email || !smtpForm.smtp_password) {
      setToast({ message: language === 'hi' ? 'Email और Password ज़रूरी हैं' : 'Email and Password required', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/api/platform-settings/smtp', smtpForm, token);
      if (res.success) {
        setToast({ message: language === 'hi' ? 'SMTP credentials save हो गए!' : 'SMTP credentials saved!', type: 'success' });
        setShowSmtpForm(false);
        loadPlatformSettings();
      } else {
        setToast({ message: res.detail || 'Failed to save', type: 'error' });
      }
    } catch (err) {
      setToast({ message: 'Save failed: ' + err.message, type: 'error' });
    }
    setSaving(false);
  };

  const testSmtp = async () => {
    setSmtpTesting(true);
    setSmtpTestResult(null);
    try {
      const res = await api.post('/api/platform-settings/smtp/test', {}, token);
      setSmtpTestResult(res);
      if (res.success) {
        setToast({ message: res.message, type: 'success' });
      } else {
        setToast({ message: res.message, type: 'error' });
      }
    } catch (err) {
      setSmtpTestResult({ success: false, message: 'Test failed: ' + err.message });
    }
    setSmtpTesting(false);
  };

  const removeSmtp = async () => {
    if (!window.confirm(language === 'hi' ? 'SMTP credentials हटाना चाहते हैं?' : 'Remove SMTP configuration?')) return;
    try {
      await api.post('/api/platform-settings/smtp', {}, token);
      const res = await fetch(`${API_URL}/api/platform-settings/smtp`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) {
        setToast({ message: language === 'hi' ? 'SMTP credentials हटा दिए गए' : 'SMTP removed', type: 'success' });
        loadPlatformSettings();
      }
    } catch (err) {
      setToast({ message: 'Remove failed', type: 'error' });
    }
  };

  // WhatsApp Business functions
  const saveWhatsappBusiness = async () => {
    if (!whatsappForm.phone_number_id || !whatsappForm.access_token) {
      setToast({ message: language === 'hi' ? 'Phone Number ID और Access Token ज़रूरी हैं' : 'Phone Number ID and Access Token required', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/api/platform-settings/whatsapp', whatsappForm, token);
      if (res.success) {
        setToast({ message: language === 'hi' ? 'WhatsApp credentials save हो गए!' : 'WhatsApp credentials saved!', type: 'success' });
        setShowWhatsappForm(false);
        loadPlatformSettings();
      }
    } catch (err) {
      setToast({ message: 'Save failed', type: 'error' });
    }
    setSaving(false);
  };

  const removeWhatsappBusiness = async () => {
    if (!window.confirm(language === 'hi' ? 'WhatsApp credentials हटाना चाहते हैं?' : 'Remove WhatsApp configuration?')) return;
    try {
      const res = await fetch(`${API_URL}/api/platform-settings/whatsapp`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) {
        setToast({ message: language === 'hi' ? 'WhatsApp credentials हटा दिए गए' : 'WhatsApp removed', type: 'success' });
        loadPlatformSettings();
      }
    } catch (err) {
      setToast({ message: 'Remove failed', type: 'error' });
    }
  };

  // Social Media functions
  const saveSocialMedia = async () => {
    setSaving(true);
    try {
      const res = await api.post('/api/platform-settings/social-media', { platform: socialPlatform, ...socialForm }, token);
      if (res.success) {
        setToast({ message: res.message, type: 'success' });
        setShowSocialForm(false);
        setSocialForm({ api_key: '', api_secret: '', access_token: '', page_id: '' });
        loadPlatformSettings();
      }
    } catch (err) {
      setToast({ message: 'Save failed', type: 'error' });
    }
    setSaving(false);
  };

  const removeSocialMedia = async (platform) => {
    if (!window.confirm(`Remove ${platform} configuration?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/platform-settings/social-media/${platform}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) {
        setToast({ message: `${platform} removed`, type: 'success' });
        loadPlatformSettings();
      }
    } catch (err) {
      setToast({ message: 'Remove failed', type: 'error' });
    }
  };

  // Connection History functions
  const loadConnectionHistory = async (filter = '') => {
    setHistoryLoading(true);
    try {
      const url = filter ? `/api/platform-settings/history?service=${filter}` : '/api/platform-settings/history';
      const res = await api.get(url, token);
      if (res?.success) {
        setConnectionLogs(res.logs || []);
        setEmailStats(res.email_stats || { total_sent: 0, total_newsletters: 0 });
      }
    } catch (err) {
      console.error('Error loading history:', err);
    }
    setHistoryLoading(false);
  };

  const clearHistory = async () => {
    if (!window.confirm(language === 'hi' ? 'पूरी history clear करना चाहते हैं?' : 'Clear all connection history?')) return;
    try {
      const res = await fetch(`${API_URL}/api/platform-settings/history/clear`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) {
        setToast({ message: language === 'hi' ? 'History clear हो गई' : 'History cleared', type: 'success' });
        setConnectionLogs([]);
      }
    } catch (err) {
      setToast({ message: 'Clear failed', type: 'error' });
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [intRes, profileRes, presetsRes, teamRes, ctaRes, galleryRes, categoriesRes] = await Promise.all([
        api.get('/api/integrations/', token),
        schoolId ? api.get(`/api/school-profile/${schoolId}`, token) : Promise.resolve({}),
        api.get('/api/school-profile/presets/colors', token),
        schoolId ? api.get(`/api/school-profile/${schoolId}/team`, token) : Promise.resolve({}),
        schoolId ? api.get(`/api/school-profile/${schoolId}/cta`, token) : Promise.resolve({}),
        schoolId ? api.get(`/api/school-profile/${schoolId}/gallery`, token) : Promise.resolve({}),
        api.get('/api/school-profile/gallery/categories', token)
      ]);
      setIntegrations(intRes);
      if (profileRes?.profile) {
        setProfile(profileRes.profile);
        setBasicInfo(profileRes.profile.basic_info || {});
        setContactInfo(profileRes.profile.contact_info || {});
        setSocialLinks(profileRes.profile.social_links || {});
        setBranding(profileRes.profile.branding || {});
        setHomeSections(profileRes.profile.home_sections || []);
      }
      // Set school subdomain for public page link
      if (profileRes?.subdomain) {
        setSchoolSubdomain(profileRes.subdomain);
      }
      setColorPresets(presetsRes?.presets || []);
      setTeamMembers(teamRes?.team_members || []);
      setCallToActions(ctaRes?.call_to_actions || []);
      setGalleryImages(galleryRes?.images || []);
      setGalleryVideos(galleryRes?.videos || []);
      setGalleryAlbums(galleryRes?.albums || []);
      setGalleryCategories(categoriesRes?.categories || galleryRes?.categories || {});
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  // ===== Meta Connection (Facebook Ads + WhatsApp) =====
  const saveMeta = async () => {
    const payload = Object.fromEntries(Object.entries(metaForm).filter(([, v]) => v && v.trim()));
    if (Object.keys(payload).length === 0) {
      setToast({ message: language === 'hi' ? 'कम से कम एक field भरें' : 'Fill at least one field', type: 'error' });
      return;
    }
    setMetaSaving(true);
    const res = await api.post('/api/integrations/meta', payload, token);
    setMetaSaving(false);
    if (res?.success) {
      setToast({ message: language === 'hi' ? 'Meta connection सेव हो गया ✅' : 'Meta connection saved ✅', type: 'success' });
      setMetaForm({ fb_ad_account_id: '', fb_page_id: '', fb_access_token: '', wa_phone_number_id: '', wa_access_token: '', wa_business_account_id: '' });
      loadData();
    } else {
      setToast({ message: res?.detail || 'Save failed', type: 'error' });
    }
  };

  const removeMeta = async (scope) => {
    const res = await api.delete(`/api/integrations/meta?scope=${scope}`, token);
    if (res?.success) {
      setToast({ message: language === 'hi' ? 'हटा दिया गया' : 'Removed', type: 'success' });
      loadData();
    }
  };

  const [metaTesting, setMetaTesting] = useState(false);
  const [metaTest, setMetaTest] = useState(null);
  const testMeta = async () => {
    setMetaTesting(true);
    setMetaTest(null);
    const res = await api.post('/api/integrations/meta/test', {}, token);
    setMetaTesting(false);
    if (res?.success) setMetaTest({ fb: res.fb, wa: res.wa });
    else setToast({ message: res?.detail || 'Test failed', type: 'error' });
  };


  // Gallery Helper Functions
  const saveGalleryPhoto = async () => {
    try {
      const formData = new FormData();
      if (editingImage) {
        // Update existing image
        Object.entries(photoForm).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, typeof value === 'boolean' ? value.toString() : value);
          }
        });
        const res = await fetch(`${API_URL}/api/school-profile/${user.school_id}/gallery/${editingImage.id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        const data = await res.json();
        if (data.success) {
          setToast({ message: language === 'hi' ? 'फोटो अपडेट हो गई!' : 'Photo updated!', type: 'success' });
        }
      } else {
        // Add new image
        Object.entries(photoForm).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, typeof value === 'boolean' ? value.toString() : value);
          }
        });
        const res = await fetch(`${API_URL}/api/school-profile/${user.school_id}/gallery/add`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        const data = await res.json();
        if (data.success) {
          setToast({ message: language === 'hi' ? 'फोटो जोड़ी गई!' : 'Photo added!', type: 'success' });
        }
      }
      setShowPhotoModal(false);
      setEditingImage(null);
      setPhotoForm({});
      loadData();
    } catch (err) {
      setToast({ message: 'Error: ' + err.message, type: 'error' });
    }
  };

  const saveGalleryVideo = async () => {
    try {
      const formData = new FormData();
      Object.entries(videoForm).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, typeof value === 'boolean' ? value.toString() : value);
        }
      });
      const res = await fetch(`${API_URL}/api/school-profile/${user.school_id}/gallery/video`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setToast({ message: language === 'hi' ? 'वीडियो जोड़ा गया!' : 'Video added!', type: 'success' });
        setShowVideoModal(false);
        setVideoForm({});
        loadData();
      }
    } catch (err) {
      setToast({ message: 'Error: ' + err.message, type: 'error' });
    }
  };

  const createAlbum = async () => {
    try {
      const formData = new FormData();
      Object.entries(albumForm).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });
      const res = await fetch(`${API_URL}/api/school-profile/${user.school_id}/gallery/album`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setToast({ message: language === 'hi' ? 'एल्बम बन गया!' : 'Album created!', type: 'success' });
        setShowAlbumModal(false);
        setAlbumForm({});
        loadData();
      }
    } catch (err) {
      setToast({ message: 'Error: ' + err.message, type: 'error' });
    }
  };

  const deleteGalleryImage = async (imageId) => {
    if (!window.confirm(language === 'hi' ? 'क्या आप यह फोटो हटाना चाहते हैं?' : 'Delete this photo?')) return;
    try {
      const res = await fetch(`${API_URL}/api/school-profile/${user.school_id}/gallery/${imageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setToast({ message: language === 'hi' ? 'फोटो हटा दी गई!' : 'Photo deleted!', type: 'success' });
        loadData();
      }
    } catch (err) {
      setToast({ message: 'Error: ' + err.message, type: 'error' });
    }
  };

  const deleteGalleryVideo = async (videoId) => {
    if (!window.confirm(language === 'hi' ? 'क्या आप यह वीडियो हटाना चाहते हैं?' : 'Delete this video?')) return;
    try {
      const res = await fetch(`${API_URL}/api/school-profile/${user.school_id}/gallery/video/${videoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setToast({ message: language === 'hi' ? 'वीडियो हटा दिया गया!' : 'Video deleted!', type: 'success' });
        loadData();
      }
    } catch (err) {
      setToast({ message: 'Error: ' + err.message, type: 'error' });
    }
  };

  const deleteAlbum = async (albumId) => {
    if (!window.confirm(language === 'hi' ? 'क्या आप यह एल्बम हटाना चाहते हैं?' : 'Delete this album?')) return;
    try {
      const res = await fetch(`${API_URL}/api/school-profile/${user.school_id}/gallery/album/${albumId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setToast({ message: language === 'hi' ? 'एल्बम हटा दिया गया!' : 'Album deleted!', type: 'success' });
        loadData();
      }
    } catch (err) {
      setToast({ message: 'Error: ' + err.message, type: 'error' });
    }
  };

  const toggleFeatured = async (imageId, isFeatured) => {
    try {
      const res = await fetch(`${API_URL}/api/school-profile/${user.school_id}/gallery/${imageId}/featured?is_featured=${isFeatured}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setToast({ message: isFeatured ? (language === 'hi' ? 'Featured में जोड़ा गया!' : 'Added to featured!') : (language === 'hi' ? 'Featured से हटाया गया!' : 'Removed from featured!'), type: 'success' });
        loadData();
      }
    } catch (err) {
      setToast({ message: 'Error: ' + err.message, type: 'error' });
    }
  };

  // Logo upload handler
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 2 * 1024 * 1024) {
      setToast({ message: 'Logo size should be less than 2MB', type: 'error' });
      return;
    }
    
    setSaving(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const formData = new FormData();
        formData.append('logo_data', reader.result);
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/school-profile/${schoolId}/logo`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        setToast({ message: language === 'hi' ? 'लोगो अपलोड हो गया!' : 'Logo uploaded!', type: 'success' });
        loadData();
      } catch (err) {
        setToast({ message: 'Upload failed', type: 'error' });
      }
      setSaving(false);
    };
    reader.readAsDataURL(file);
  };

  // Save profile
  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put(`/api/school-profile/${schoolId}`, {
        basic_info: basicInfo,
        contact_info: contactInfo,
        social_links: socialLinks,
        branding: branding
      }, token);
      setToast({ message: language === 'hi' ? 'प्रोफाइल सेव हो गई!' : 'Profile saved!', type: 'success' });
    } catch (err) {
      setToast({ message: 'Save failed', type: 'error' });
    }
    setSaving(false);
  };

  // Save home sections
  const saveHomeSections = async () => {
    setSaving(true);
    try {
      await api.put(`/api/school-profile/${schoolId}/home-sections`, { sections: homeSections }, token);
      setToast({ message: language === 'hi' ? 'होम पेज सेव हो गया!' : 'Home page saved!', type: 'success' });
    } catch (err) {
      setToast({ message: 'Save failed', type: 'error' });
    }
    setSaving(false);
  };

  // Change Plan
  const handlePlanChange = async () => {
    if (!selectedNewPlan || !schoolId) return;
    setChangingPlan(true);
    try {
      const res = await api.post(`/api/plans/select/${schoolId}`, { plan_name: selectedNewPlan }, token);
      if (res.success) {
        setCurrentPlan(res.plan);
        setToast({ message: language === 'hi' ? `Plan ${selectedNewPlan} में बदल गया!` : `Plan changed to ${selectedNewPlan}!`, type: 'success' });
        setShowPlanModal(false);
        // Update user in localStorage
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        storedUser.school_plan = selectedNewPlan;
        localStorage.setItem('user', JSON.stringify(storedUser));
      }
    } catch (err) {
      setToast({ message: language === 'hi' ? 'Plan बदलने में error' : 'Error changing plan', type: 'error' });
    }
    setChangingPlan(false);
  };

  // Team Member functions
  const handleTeamPhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1 * 1024 * 1024) {
      setToast({ message: 'Photo size should be less than 1MB', type: 'error' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setTeamForm(prev => ({ ...prev, photo_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const saveTeamMember = async () => {
    if (!teamForm.name || !teamForm.role) {
      setToast({ message: language === 'hi' ? 'नाम और पद भरें' : 'Name and role required', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      if (editingMember) {
        await api.put(`/api/school-profile/${schoolId}/team/${editingMember.member_id}`, teamForm, token);
        setTeamMembers(prev => prev.map(m => m.member_id === editingMember.member_id ? { ...m, ...teamForm } : m));
      } else {
        const res = await api.post(`/api/school-profile/${schoolId}/team`, teamForm, token);
        if (res.member) setTeamMembers(prev => [...prev, res.member]);
      }
      setToast({ message: language === 'hi' ? 'सदस्य सेव हो गया' : 'Member saved', type: 'success' });
      setShowTeamModal(false);
      setEditingMember(null);
      setTeamForm({ name: '', name_hindi: '', role: '', role_hindi: '', qualification: '', experience: '', phone: '', email: '', order: 0 });
    } catch (err) {
      setToast({ message: 'Save failed', type: 'error' });
    }
    setSaving(false);
  };

  const deleteTeamMember = async (memberId) => {
    if (!window.confirm(language === 'hi' ? 'क्या आप सच में हटाना चाहते हैं?' : 'Are you sure you want to delete?')) return;
    try {
      await api.del(`/api/school-profile/${schoolId}/team/${memberId}`, token);
      setTeamMembers(prev => prev.filter(m => m.member_id !== memberId));
      setToast({ message: language === 'hi' ? 'सदस्य हटा दिया गया' : 'Member deleted', type: 'success' });
    } catch (err) {
      setToast({ message: 'Delete failed', type: 'error' });
    }
  };

  // CTA functions
  const updateCTA = (ctaId, field, value) => {
    setCallToActions(prev => prev.map(c => c.cta_id === ctaId ? { ...c, [field]: value } : c));
  };

  const saveCTAs = async () => {
    setSaving(true);
    try {
      await api.put(`/api/school-profile/${schoolId}/cta`, callToActions, token);
      setToast({ message: language === 'hi' ? 'CTA बटन सेव हो गए' : 'CTA buttons saved', type: 'success' });
    } catch (err) {
      setToast({ message: 'Save failed', type: 'error' });
    }
    setSaving(false);
  };

  const addNewCTA = () => {
    const newCta = {
      cta_id: `cta_${Date.now()}`,
      label: 'New Button',
      label_hindi: 'नया बटन',
      action_type: 'link',
      action_value: '',
      icon: '🔗',
      is_primary: false,
      is_visible: true,
      order: callToActions.length + 1
    };
    setCallToActions(prev => [...prev, newCta]);
  };

  // Toggle section visibility
  const toggleSection = (sectionId) => {
    setHomeSections(prev => prev.map(s => 
      s.section_id === sectionId ? { ...s, is_visible: !s.is_visible } : s
    ));
  };

  // Update section content
  const updateSectionContent = (sectionId, field, value) => {
    setHomeSections(prev => prev.map(s =>
      s.section_id === sectionId ? { ...s, [field]: value } : s
    ));
  };

  // Apply color preset
  const applyColorPreset = (preset) => {
    setBranding({
      ...branding,
      primary_color: preset.primary_color,
      secondary_color: preset.secondary_color,
      accent_color: preset.accent_color
    });
    setToast({ message: `${preset.name} theme applied!`, type: 'success' });
  };

  const saveTwilio = async () => {
    if (!twilioForm.account_sid || !twilioForm.auth_token || !twilioForm.whatsapp_number) {
      setToast({ message: 'All fields required', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/api/integrations/twilio', twilioForm, token);
      if (res.success) {
        setToast({ message: 'Twilio configured successfully!', type: 'success' });
        setShowTwilioForm(false);
        setTwilioForm({ account_sid: '', auth_token: '', whatsapp_number: '' });
        loadData();
      } else {
        setToast({ message: res.detail || 'Failed to save', type: 'error' });
      }
    } catch (err) {
      setToast({ message: 'Invalid credentials', type: 'error' });
    }
    setSaving(false);
  };

  const saveRazorpay = async () => {
    if (!razorpayForm.key_id || !razorpayForm.key_secret) {
      setToast({ message: 'All fields required', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/api/integrations/razorpay?key_id=${encodeURIComponent(razorpayForm.key_id)}&key_secret=${encodeURIComponent(razorpayForm.key_secret)}`, {}, token);
      if (res.success) {
        setToast({ message: 'Razorpay configured successfully!', type: 'success' });
        setShowRazorpayForm(false);
        setRazorpayForm({ key_id: '', key_secret: '' });
        loadData();
      }
    } catch (err) {
      setToast({ message: 'Failed to save', type: 'error' });
    }
    setSaving(false);
  };

  const removeTwilio = async () => {
    if (!window.confirm('Remove Twilio configuration?')) return;
    await api.delete('/api/integrations/twilio', token);
    setToast({ message: 'Twilio removed', type: 'success' });
    loadData();
  };

  const removeRazorpay = async () => {
    if (!window.confirm('Remove Razorpay configuration?')) return;
    await api.delete('/api/integrations/razorpay', token);
    setToast({ message: 'Razorpay removed', type: 'success' });
    loadData();
  };

  const downloadRazorpayGuide = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/integrations/razorpay/setup-guide`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setToast({ message: language === 'hi' ? 'Guide download नहीं हो सकी' : 'Guide download failed', type: 'error' }); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'Shiksha_Mitra_Razorpay_Setup.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setToast({ message: language === 'hi' ? '✅ Razorpay Setup Guide download हो गई' : '✅ Razorpay Setup Guide downloaded', type: 'success' });
    } catch { setToast({ message: language === 'hi' ? 'Download में त्रुटि' : 'Download error', type: 'error' }); }
  };


  const testTwilio = async () => {
    const res = await api.post('/api/integrations/twilio/test', {}, token);
    setToast({ message: res.message, type: res.success ? 'success' : 'error' });
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-2xl font-bold text-gray-800">{language === 'hi' ? '⚙️ सेटिंग्स' : '⚙️ Settings'}</h2>
        {schoolSubdomain && (
          <a 
            href={`/school/${schoolSubdomain}`} 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 shadow-md flex items-center gap-2 font-medium"
            data-testid="view-public-page-btn"
          >
            🌐 {language === 'hi' ? 'पब्लिक पेज देखें' : 'View Public Page'}
          </a>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2 overflow-x-auto">
        {[
          { id: 'profile', label: language === 'hi' ? 'स्कूल प्रोफाइल' : 'School Profile', icon: '🏫' },
          { id: 'branding', label: language === 'hi' ? 'ब्रांडिंग' : 'Branding', icon: '🎨' },
          { id: 'team', label: language === 'hi' ? 'टीम' : 'Team', icon: '👥' },
          { id: 'gallery', label: language === 'hi' ? 'गैलरी' : 'Gallery', icon: '📸' },
          { id: 'cta', label: language === 'hi' ? 'CTA बटन' : 'CTA Buttons', icon: '📢' },
          { id: 'preview', label: language === 'hi' ? 'प्रीव्यू' : 'Preview', icon: '👁️' },
          { id: 'plan', label: language === 'hi' ? 'प्लान' : 'Plan', icon: '📊' },
          { id: 'integrations', label: language === 'hi' ? 'Integrations' : 'Integrations', icon: '🔗' },
          { id: 'history', label: language === 'hi' ? 'History' : 'History', icon: '📜' }
        ].map(tab => (
          <button key={tab.id} onClick={() => { setActiveTab(tab.id); if (tab.id === 'history') loadConnectionHistory(historyFilter); }}
            data-testid={`settings-tab-${tab.id}`}
            className={`px-4 py-2 rounded-t-lg transition whitespace-nowrap ${activeTab === tab.id ? 'bg-orange-100 text-orange-700 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="space-y-6">
          {/* Logo Upload */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold text-lg mb-4">{language === 'hi' ? '🖼️ स्कूल लोगो' : '🖼️ School Logo'}</h3>
            <div className="flex items-center gap-6">
              <div className="w-32 h-32 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50">
                {profile?.logo_url ? (
                  <img src={profile.logo_url} alt="School Logo" className="w-full h-full object-contain" />
                ) : (
                  <span className="text-4xl text-gray-300">🏫</span>
                )}
              </div>
              <div>
                <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="logo-upload" />
                <label htmlFor="logo-upload" className="px-4 py-2 bg-orange-500 text-white rounded-lg cursor-pointer hover:bg-orange-600">
                  {saving ? '...' : (language === 'hi' ? 'लोगो अपलोड करें' : 'Upload Logo')}
                </label>
                <p className="text-sm text-gray-500 mt-2">{language === 'hi' ? 'Max 2MB, PNG/JPG' : 'Max 2MB, PNG/JPG'}</p>
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold text-lg mb-4">{language === 'hi' ? '📋 मूल जानकारी' : '📋 Basic Information'}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'विद्यालय का नाम' : 'School Name'}</label>
                <input value={basicInfo.name || ''} onChange={e => setBasicInfo({...basicInfo, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg" placeholder="ABC Public School" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'विद्यालय का नाम (हिंदी)' : 'School Name (Hindi)'}</label>
                <input value={basicInfo.name_hindi || ''} onChange={e => setBasicInfo({...basicInfo, name_hindi: e.target.value})} className="w-full px-4 py-2 border rounded-lg" placeholder="ए.बी.सी. पब्लिक स्कूल" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'टैगलाइन' : 'Tagline'}</label>
                <input value={basicInfo.tagline || ''} onChange={e => setBasicInfo({...basicInfo, tagline: e.target.value})} className="w-full px-4 py-2 border rounded-lg" placeholder="Excellence in Education" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'स्थापना वर्ष' : 'Established Year'}</label>
                <input type="number" value={basicInfo.established_year || ''} onChange={e => setBasicInfo({...basicInfo, established_year: parseInt(e.target.value)})} className="w-full px-4 py-2 border rounded-lg" placeholder="2000" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'संबद्धता बोर्ड' : 'Affiliation Board'}</label>
                <select value={basicInfo.affiliation_board || ''} onChange={e => setBasicInfo({...basicInfo, affiliation_board: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
                  <option value="">{language === 'hi' ? 'चुनें' : 'Select'}</option>
                  <option value="CBSE">CBSE</option>
                  <option value="ICSE">ICSE</option>
                  <option value="State Board">State Board</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'संबद्धता क्रमांक' : 'Affiliation Number'}</label>
                <input value={basicInfo.affiliation_number || ''} onChange={e => setBasicInfo({...basicInfo, affiliation_number: e.target.value})} className="w-full px-4 py-2 border rounded-lg" placeholder="123456" />
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold text-lg mb-4">{language === 'hi' ? '📍 संपर्क जानकारी' : '📍 Contact Information'}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'पता' : 'Address'}</label>
                <textarea value={contactInfo.address || ''} onChange={e => setContactInfo({...contactInfo, address: e.target.value})} className="w-full px-4 py-2 border rounded-lg h-20" placeholder="123, Main Street, City" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'शहर' : 'City'}</label>
                <input value={contactInfo.city || ''} onChange={e => setContactInfo({...contactInfo, city: e.target.value})} className="w-full px-4 py-2 border rounded-lg" placeholder="New Delhi" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'राज्य' : 'State'}</label>
                <input value={contactInfo.state || ''} onChange={e => setContactInfo({...contactInfo, state: e.target.value})} className="w-full px-4 py-2 border rounded-lg" placeholder="Delhi" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'पिनकोड' : 'Pincode'}</label>
                <input value={contactInfo.pincode || ''} onChange={e => setContactInfo({...contactInfo, pincode: e.target.value})} className="w-full px-4 py-2 border rounded-lg" placeholder="110001" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'फोन' : 'Phone'}</label>
                <input value={contactInfo.phone || ''} onChange={e => setContactInfo({...contactInfo, phone: e.target.value})} className="w-full px-4 py-2 border rounded-lg" placeholder="+91 11 12345678" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'ईमेल' : 'Email'}</label>
                <input type="email" value={contactInfo.email || ''} onChange={e => setContactInfo({...contactInfo, email: e.target.value})} className="w-full px-4 py-2 border rounded-lg" placeholder="contact@school.edu" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'वेबसाइट' : 'Website'}</label>
                <input value={contactInfo.website || ''} onChange={e => setContactInfo({...contactInfo, website: e.target.value})} className="w-full px-4 py-2 border rounded-lg" placeholder="www.school.edu" />
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold text-lg mb-4">{language === 'hi' ? '🌐 सोशल मीडिया' : '🌐 Social Media'}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'facebook', label: 'Facebook', icon: '📘', placeholder: 'https://facebook.com/yourschool' },
                { key: 'instagram', label: 'Instagram', icon: '📸', placeholder: 'https://instagram.com/yourschool' },
                { key: 'twitter', label: 'Twitter/X', icon: '🐦', placeholder: 'https://twitter.com/yourschool' },
                { key: 'youtube', label: 'YouTube', icon: '📺', placeholder: 'https://youtube.com/yourschool' },
                { key: 'linkedin', label: 'LinkedIn', icon: '💼', placeholder: 'https://linkedin.com/company/yourschool' },
                { key: 'whatsapp', label: 'WhatsApp', icon: '💬', placeholder: '+91 9876543210' }
              ].map(social => (
                <div key={social.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{social.icon} {social.label}</label>
                  <input value={socialLinks[social.key] || ''} onChange={e => setSocialLinks({...socialLinks, [social.key]: e.target.value})} className="w-full px-4 py-2 border rounded-lg" placeholder={social.placeholder} />
                </div>
              ))}
            </div>
          </div>

          <button onClick={saveProfile} disabled={saving} className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 font-medium">
            {saving ? '...' : (language === 'hi' ? '💾 प्रोफाइल सेव करें' : '💾 Save Profile')}
          </button>
        </div>
      )}

      {/* Branding Tab */}
      {activeTab === 'branding' && (
        <div className="space-y-6">
          {/* Color Theme */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold text-lg mb-4">{language === 'hi' ? '🎨 रंग थीम' : '🎨 Color Theme'}</h3>
            
            {/* Color Presets */}
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-3">{language === 'hi' ? 'प्रीसेट थीम चुनें:' : 'Choose a preset theme:'}</p>
              <div className="flex gap-3 flex-wrap">
                {colorPresets.map(preset => (
                  <button key={preset.id} onClick={() => applyColorPreset(preset)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border hover:border-orange-400 transition">
                    <div className="flex gap-1">
                      <div className="w-4 h-4 rounded-full" style={{backgroundColor: preset.primary_color}}></div>
                      <div className="w-4 h-4 rounded-full" style={{backgroundColor: preset.secondary_color}}></div>
                      <div className="w-4 h-4 rounded-full" style={{backgroundColor: preset.accent_color}}></div>
                    </div>
                    <span className="text-sm">{language === 'hi' ? preset.name_hi : preset.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Colors */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'प्राथमिक रंग' : 'Primary Color'}</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={branding.primary_color || '#FF6B35'} onChange={e => setBranding({...branding, primary_color: e.target.value})} className="w-12 h-10 rounded cursor-pointer" />
                  <input value={branding.primary_color || '#FF6B35'} onChange={e => setBranding({...branding, primary_color: e.target.value})} className="flex-1 px-3 py-2 border rounded-lg font-mono text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'द्वितीयक रंग' : 'Secondary Color'}</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={branding.secondary_color || '#F7931E'} onChange={e => setBranding({...branding, secondary_color: e.target.value})} className="w-12 h-10 rounded cursor-pointer" />
                  <input value={branding.secondary_color || '#F7931E'} onChange={e => setBranding({...branding, secondary_color: e.target.value})} className="flex-1 px-3 py-2 border rounded-lg font-mono text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'एक्सेंट रंग' : 'Accent Color'}</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={branding.accent_color || '#4A90A4'} onChange={e => setBranding({...branding, accent_color: e.target.value})} className="w-12 h-10 rounded cursor-pointer" />
                  <input value={branding.accent_color || '#4A90A4'} onChange={e => setBranding({...branding, accent_color: e.target.value})} className="flex-1 px-3 py-2 border rounded-lg font-mono text-sm" />
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="mt-6 p-4 rounded-xl border" style={{background: `linear-gradient(135deg, ${branding.primary_color || '#FF6B35'}, ${branding.secondary_color || '#F7931E'})`}}>
              <h4 className="text-white font-bold text-lg">{basicInfo.name || 'Your School Name'}</h4>
              <p className="text-white/80 text-sm">{basicInfo.tagline || 'Your School Tagline'}</p>
              <button className="mt-3 px-4 py-2 rounded-lg text-sm font-medium" style={{backgroundColor: branding.accent_color || '#4A90A4', color: 'white'}}>
                {language === 'hi' ? 'प्रवेश हेतु आवेदन करें' : 'Apply for Admission'}
              </button>
            </div>
          </div>

          <button onClick={saveProfile} disabled={saving} className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 font-medium">
            {saving ? '...' : (language === 'hi' ? '💾 ब्रांडिंग सेव करें' : '💾 Save Branding')}
          </button>
        </div>
      )}

      {/* Team Management Tab */}
      {activeTab === 'team' && (
        <div className="space-y-6" data-testid="team-tab">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-semibold text-lg">{language === 'hi' ? '👥 हमारी टीम' : '👥 Our Team'}</h3>
              <p className="text-sm text-gray-500">{language === 'hi' ? 'Principal, Teachers और Staff की जानकारी जोड़ें' : 'Add Principal, Teachers and Staff details'}</p>
            </div>
            <button onClick={() => { setEditingMember(null); setTeamForm({ name: '', name_hindi: '', role: '', role_hindi: '', qualification: '', experience: '', phone: '', email: '', order: teamMembers.length }); setShowTeamModal(true); }}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
              data-testid="add-team-member-btn">
              + {language === 'hi' ? 'सदस्य जोड़ें' : 'Add Member'}
            </button>
          </div>

          {/* Team Members Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teamMembers.map((member, idx) => (
              <div key={member.member_id || idx} className="bg-white rounded-xl shadow p-4 relative group">
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition flex gap-1">
                  <button onClick={() => { setEditingMember(member); setTeamForm(member); setShowTeamModal(true); }}
                    className="p-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 text-sm">✏️</button>
                  <button onClick={() => deleteTeamMember(member.member_id)}
                    className="p-1 bg-red-100 text-red-600 rounded hover:bg-red-200 text-sm">🗑️</button>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center overflow-hidden">
                    {member.photo_url ? (
                      <img src={member.photo_url} alt={member.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-3xl">👤</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800">{language === 'hi' && member.name_hindi ? member.name_hindi : member.name}</h4>
                    <p className="text-sm text-orange-600 font-medium">{language === 'hi' && member.role_hindi ? member.role_hindi : member.role}</p>
                    {member.qualification && <p className="text-xs text-gray-500">{member.qualification}</p>}
                    {member.experience && <p className="text-xs text-gray-400">{member.experience}</p>}
                  </div>
                </div>
                {(member.phone || member.email) && (
                  <div className="mt-3 pt-3 border-t flex gap-3 text-xs text-gray-500">
                    {member.phone && <span>📞 {member.phone}</span>}
                    {member.email && <span>📧 {member.email}</span>}
                  </div>
                )}
              </div>
            ))}
            
            {teamMembers.length === 0 && (
              <div className="col-span-full text-center py-12 bg-gray-50 rounded-xl">
                <span className="text-4xl">👥</span>
                <p className="text-gray-500 mt-2">{language === 'hi' ? 'अभी कोई टीम सदस्य नहीं' : 'No team members yet'}</p>
                <p className="text-sm text-gray-400">{language === 'hi' ? 'Principal, Teachers जोड़ें' : 'Add Principal, Teachers'}</p>
              </div>
            )}
          </div>

          {/* Suggested Roles */}
          <div className="bg-blue-50 rounded-xl p-4">
            <p className="text-sm font-medium text-blue-700 mb-2">{language === 'hi' ? '💡 सुझाव: ये पद जोड़ें' : '💡 Suggestion: Add these roles'}</p>
            <div className="flex flex-wrap gap-2">
              {['Principal / प्रधानाचार्य', 'Vice Principal / उप-प्रधानाचार्य', 'Head Teacher / मुख्य अध्यापक', 'Senior Teacher / वरिष्ठ अध्यापक', 'Accountant / लेखाकार', 'Librarian / पुस्तकालयाध्यक्ष'].map(role => (
                <span key={role} className="px-3 py-1 bg-white text-blue-600 rounded-full text-xs border border-blue-200">{role}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Gallery Tab - Enhanced with 7 Features */}
      {activeTab === 'gallery' && (
        <div className="space-y-6" data-testid="gallery-tab">
          {/* Tab Header */}
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h3 className="font-semibold text-lg">{language === 'hi' ? '📸 फोटो और वीडियो गैलरी' : '📸 Photo & Video Gallery'}</h3>
              <p className="text-sm text-gray-500">{language === 'hi' ? 'स्कूल की तस्वीरें और वीडियो - इवेंट्स, बिल्डिंग, एक्टिविटीज' : 'School photos and videos - events, building, activities'}</p>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => setShowVideoModal(true)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                🎬 {language === 'hi' ? 'वीडियो जोड़ें' : 'Add Video'}
              </button>
              <button 
                onClick={() => setShowPhotoModal(true)}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
              >
                📸 {language === 'hi' ? 'फोटो जोड़ें' : 'Add Photo'}
              </button>
            </div>
          </div>

          {/* Gallery Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="gallery-stats">
            <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-100">
              <p className="text-2xl font-bold text-blue-600">{galleryImages.length}</p>
              <p className="text-xs text-blue-500">{language === 'hi' ? 'फोटो' : 'Photos'}</p>
            </div>
            <div className="bg-red-50 rounded-xl p-3 text-center border border-red-100">
              <p className="text-2xl font-bold text-red-600">{galleryVideos.length}</p>
              <p className="text-xs text-red-500">{language === 'hi' ? 'वीडियो' : 'Videos'}</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-3 text-center border border-purple-100">
              <p className="text-2xl font-bold text-purple-600">{galleryAlbums.length}</p>
              <p className="text-xs text-purple-500">{language === 'hi' ? 'एल्बम' : 'Albums'}</p>
            </div>
            <div className="bg-yellow-50 rounded-xl p-3 text-center border border-yellow-100">
              <p className="text-2xl font-bold text-yellow-600">{galleryImages.filter(i => i.is_featured).length}</p>
              <p className="text-xs text-yellow-500">{language === 'hi' ? 'Featured' : 'Featured'}</p>
            </div>
          </div>

          {/* Sub-tabs: Photos, Videos, Albums */}
          <div className="flex gap-2 border-b">
            {['photos', 'videos', 'albums'].map(tab => (
              <button 
                key={tab}
                onClick={() => setGallerySubTab(tab)}
                className={`px-4 py-2 font-medium ${gallerySubTab === tab ? 'text-orange-600 border-b-2 border-orange-500' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {tab === 'photos' && (language === 'hi' ? '📷 फोटो' : '📷 Photos')}
                {tab === 'videos' && (language === 'hi' ? '🎬 वीडियो' : '🎬 Videos')}
                {tab === 'albums' && (language === 'hi' ? '📁 एल्बम' : '📁 Albums')}
              </button>
            ))}
          </div>

          {/* Photos Tab */}
          {gallerySubTab === 'photos' && (
            <div className="space-y-4">
              {/* Category Filter */}
              <div className="flex gap-2 flex-wrap">
                <button 
                  onClick={() => setSelectedCategory(null)}
                  className={`px-3 py-1 rounded-full text-sm ${!selectedCategory ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {language === 'hi' ? 'सभी' : 'All'}
                </button>
                {Object.entries(galleryCategories).map(([key, cat]) => (
                  <button 
                    key={key}
                    onClick={() => setSelectedCategory(key)}
                    className={`px-3 py-1 rounded-full text-sm ${selectedCategory === key ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    {language === 'hi' ? cat.name_hi : cat.name}
                  </button>
                ))}
              </div>

              {/* Featured Photos Section */}
              {galleryImages.filter(img => img.is_featured).length > 0 && !selectedCategory && (
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4">
                  <h4 className="font-medium text-orange-700 mb-3">⭐ {language === 'hi' ? 'Featured फोटोज' : 'Featured Photos'}</h4>
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {galleryImages.filter(img => img.is_featured).slice(0, 6).map((img, idx) => (
                      <div key={img.id} className="flex-shrink-0 w-32 h-32 rounded-lg overflow-hidden shadow-md">
                        <img src={img.url} alt={img.caption} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Photo Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {galleryImages
                  .filter(img => !selectedCategory || img.category === selectedCategory)
                  .map((image, idx) => (
                  <div key={image.id || idx} className="relative group rounded-xl overflow-hidden shadow-lg bg-white">
                    <div className="aspect-square">
                      <img src={image.url} alt={image.caption || `Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                    </div>
                    {/* Featured Badge */}
                    {image.is_featured && (
                      <div className="absolute top-2 left-2 px-2 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full">⭐</div>
                    )}
                    {/* Category Badge */}
                    {image.category && galleryCategories[image.category] && (
                      <div className="absolute bottom-12 left-2 px-2 py-1 bg-black/50 text-white text-xs rounded">
                        {galleryCategories[image.category]?.subcategories?.find(s => s.id === image.subcategory)?.icon || '📷'} 
                        {language === 'hi' ? galleryCategories[image.category].name_hi : galleryCategories[image.category].name}
                      </div>
                    )}
                    {/* Caption */}
                    <div className="p-2 bg-white">
                      <p className="text-sm text-gray-700 truncate">{language === 'hi' ? (image.caption_hindi || image.caption) : (image.caption || image.caption_hindi)}</p>
                      {image.event_date && <p className="text-xs text-gray-400">📅 {image.event_date}</p>}
                    </div>
                    {/* Action Buttons */}
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => toggleFeatured(image.id, !image.is_featured)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${image.is_featured ? 'bg-yellow-400 text-yellow-900' : 'bg-white text-gray-600'}`}
                        title={language === 'hi' ? 'Featured बनाएं' : 'Toggle Featured'}
                      >⭐</button>
                      <button 
                        onClick={() => { setEditingImage(image); setShowPhotoModal(true); }}
                        className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600"
                        title={language === 'hi' ? 'एडिट करें' : 'Edit'}
                      >✏️</button>
                      <button 
                        onClick={() => deleteGalleryImage(image.id)}
                        className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                        title={language === 'hi' ? 'हटाएं' : 'Delete'}
                      >×</button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Empty State */}
              {galleryImages.filter(img => !selectedCategory || img.category === selectedCategory).length === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                  <div className="text-6xl mb-4">📸</div>
                  <p className="text-gray-500 mb-2">{language === 'hi' ? 'कोई फोटो नहीं है' : 'No photos yet'}</p>
                </div>
              )}
            </div>
          )}

          {/* Videos Tab */}
          {gallerySubTab === 'videos' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {galleryVideos.map((video, idx) => (
                  <div key={video.id || idx} className="bg-white rounded-xl shadow-lg overflow-hidden group">
                    <div className="relative aspect-video">
                      {video.thumbnail ? (
                        <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center text-6xl">🎬</div>
                      )}
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                        <a href={video.video_url} target="_blank" rel="noopener noreferrer" 
                           className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center text-white text-2xl hover:bg-red-700 transition">
                          ▶
                        </a>
                      </div>
                      {/* Delete Button */}
                      <button 
                        onClick={() => deleteGalleryVideo(video.id)}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      >×</button>
                    </div>
                    <div className="p-3">
                      <h4 className="font-medium text-gray-800">{language === 'hi' ? (video.title_hindi || video.title) : video.title}</h4>
                      {video.description && <p className="text-sm text-gray-500 truncate">{language === 'hi' ? (video.description_hindi || video.description) : video.description}</p>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Empty State */}
              {galleryVideos.length === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                  <div className="text-6xl mb-4">🎬</div>
                  <p className="text-gray-500 mb-2">{language === 'hi' ? 'कोई वीडियो नहीं है' : 'No videos yet'}</p>
                  <p className="text-sm text-gray-400">{language === 'hi' ? 'YouTube वीडियो जोड़ें' : 'Add YouTube videos'}</p>
                </div>
              )}
            </div>
          )}

          {/* Albums Tab */}
          {gallerySubTab === 'albums' && (
            <div className="space-y-4">
              <button 
                onClick={() => setShowAlbumModal(true)}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
              >
                + {language === 'hi' ? 'नया एल्बम' : 'New Album'}
              </button>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {galleryAlbums.map((album, idx) => (
                  <div key={album.id || idx} className="bg-white rounded-xl shadow-lg overflow-hidden group cursor-pointer hover:shadow-xl transition">
                    <div className="aspect-square bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
                      {album.cover_image ? (
                        <img src={album.cover_image} alt={album.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-6xl">📁</div>
                      )}
                    </div>
                    <div className="p-3">
                      <h4 className="font-medium text-gray-800">{language === 'hi' ? (album.name_hindi || album.name) : album.name}</h4>
                      <p className="text-sm text-gray-500">{album.photo_count || 0} {language === 'hi' ? 'फोटोज' : 'photos'}</p>
                      {album.year && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{album.year}</span>}
                    </div>
                    {/* Delete Button */}
                    <button 
                      onClick={(e) => { e.stopPropagation(); deleteAlbum(album.id); }}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    >×</button>
                  </div>
                ))}
              </div>

              {/* Empty State */}
              {galleryAlbums.length === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                  <div className="text-6xl mb-4">📁</div>
                  <p className="text-gray-500">{language === 'hi' ? 'कोई एल्बम नहीं है' : 'No albums yet'}</p>
                </div>
              )}
            </div>
          )}

          {/* Tips Section */}
          <div className="bg-blue-50 rounded-xl p-4">
            <h4 className="font-medium text-blue-700 mb-2">💡 {language === 'hi' ? 'सुझाव' : 'Tips'}</h4>
            <ul className="text-sm text-blue-600 space-y-1">
              <li>• {language === 'hi' ? '⭐ Featured फोटोज होमपेज पर हाइलाइट होंगी' : '⭐ Featured photos will be highlighted on homepage'}</li>
              <li>• {language === 'hi' ? '📅 इवेंट डेट डालें ताकि फोटोज सही क्रम में दिखें' : '📅 Add event date for proper sorting'}</li>
              <li>• {language === 'hi' ? '🏷️ कैटेगरी चुनें (Events, Infrastructure, Achievements)' : '🏷️ Select category for better organization'}</li>
              <li>• {language === 'hi' ? '🎬 YouTube वीडियो लिंक से वीडियो जोड़ें' : '🎬 Add videos using YouTube links'}</li>
            </ul>
          </div>
        </div>
      )}

      {/* Add Photo Modal */}
      {showPhotoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-lg font-bold">{editingImage ? (language === 'hi' ? '✏️ फोटो एडिट करें' : '✏️ Edit Photo') : (language === 'hi' ? '📸 नई फोटो जोड़ें' : '📸 Add New Photo')}</h3>
              <button onClick={() => { setShowPhotoModal(false); setEditingImage(null); setPhotoForm({}); }} className="text-2xl text-gray-500 hover:text-gray-700">×</button>
            </div>
            <div className="p-4 space-y-4">
              {/* Image Upload */}
              {!editingImage && (
                <div>
                  <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'फोटो चुनें *' : 'Select Photo *'}</label>
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file && file.size <= 2 * 1024 * 1024) {
                        const reader = new FileReader();
                        reader.onload = (ev) => setPhotoForm({...photoForm, image_data: ev.target.result});
                        reader.readAsDataURL(file);
                      } else if (file) {
                        alert(language === 'hi' ? 'फाइल 2MB से कम होनी चाहिए' : 'File must be under 2MB');
                      }
                    }}
                    className="w-full p-2 border rounded-lg"
                  />
                  {photoForm.image_data && (
                    <img src={photoForm.image_data} alt="Preview" className="mt-2 h-32 object-cover rounded-lg" />
                  )}
                </div>
              )}

              {/* Caption */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'कैप्शन (English)' : 'Caption (English)'}</label>
                  <input 
                    value={photoForm.caption || editingImage?.caption || ''}
                    onChange={e => setPhotoForm({...photoForm, caption: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                    placeholder="Annual Day 2024"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'कैप्शन (Hindi)' : 'Caption (Hindi)'}</label>
                  <input 
                    value={photoForm.caption_hindi || editingImage?.caption_hindi || ''}
                    onChange={e => setPhotoForm({...photoForm, caption_hindi: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                    placeholder="वार्षिक दिवस 2024"
                  />
                </div>
              </div>

              {/* Category & Subcategory */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'कैटेगरी' : 'Category'}</label>
                  <select 
                    value={photoForm.category || editingImage?.category || ''}
                    onChange={e => setPhotoForm({...photoForm, category: e.target.value, subcategory: ''})}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="">{language === 'hi' ? 'चुनें...' : 'Select...'}</option>
                    {Object.entries(galleryCategories).map(([key, cat]) => (
                      <option key={key} value={key}>{language === 'hi' ? cat.name_hi : cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'उप-कैटेगरी' : 'Sub-Category'}</label>
                  <select 
                    value={photoForm.subcategory || editingImage?.subcategory || ''}
                    onChange={e => setPhotoForm({...photoForm, subcategory: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                    disabled={!photoForm.category && !editingImage?.category}
                  >
                    <option value="">{language === 'hi' ? 'चुनें...' : 'Select...'}</option>
                    {galleryCategories[photoForm.category || editingImage?.category]?.subcategories?.map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.icon} {language === 'hi' ? sub.name_hi : sub.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Event Date & Photographer */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'इवेंट तारीख' : 'Event Date'}</label>
                  <input 
                    type="date"
                    value={photoForm.event_date || editingImage?.event_date || ''}
                    onChange={e => setPhotoForm({...photoForm, event_date: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'फोटोग्राफर' : 'Photographer'}</label>
                  <input 
                    value={photoForm.photographer || editingImage?.photographer || ''}
                    onChange={e => setPhotoForm({...photoForm, photographer: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                    placeholder="Name"
                  />
                </div>
              </div>

              {/* Location */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'स्थान (English)' : 'Location (English)'}</label>
                  <input 
                    value={photoForm.location || editingImage?.location || ''}
                    onChange={e => setPhotoForm({...photoForm, location: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                    placeholder="School Auditorium"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'स्थान (Hindi)' : 'Location (Hindi)'}</label>
                  <input 
                    value={photoForm.location_hindi || editingImage?.location_hindi || ''}
                    onChange={e => setPhotoForm({...photoForm, location_hindi: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                    placeholder="स्कूल ऑडिटोरियम"
                  />
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'टैग्स (कॉमा से अलग करें)' : 'Tags (comma separated)'}</label>
                <input 
                  value={photoForm.tags || (editingImage?.tags?.join(',')) || ''}
                  onChange={e => setPhotoForm({...photoForm, tags: e.target.value})}
                  className="w-full p-2 border rounded-lg"
                  placeholder="annual day, 2024, cultural"
                />
              </div>

              {/* Featured Toggle */}
              <div className="flex items-center gap-3">
                <input 
                  type="checkbox"
                  id="is_featured"
                  checked={photoForm.is_featured ?? editingImage?.is_featured ?? false}
                  onChange={e => setPhotoForm({...photoForm, is_featured: e.target.checked})}
                  className="w-5 h-5 text-orange-500"
                />
                <label htmlFor="is_featured" className="font-medium">⭐ {language === 'hi' ? 'Featured फोटो (होमपेज पर दिखेगी)' : 'Featured Photo (will show on homepage)'}</label>
              </div>

              {/* Submit Button */}
              <button 
                onClick={() => saveGalleryPhoto()}
                disabled={!editingImage && !photoForm.image_data}
                className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 font-medium"
              >
                {editingImage ? (language === 'hi' ? '💾 सेव करें' : '💾 Save Changes') : (language === 'hi' ? '📸 फोटो जोड़ें' : '📸 Add Photo')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">{language === 'hi' ? '🎬 YouTube वीडियो जोड़ें' : '🎬 Add YouTube Video'}</h3>
              <button onClick={() => { setShowVideoModal(false); setVideoForm({}); }} className="text-2xl text-gray-500 hover:text-gray-700">×</button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'YouTube URL *' : 'YouTube URL *'}</label>
                <input 
                  value={videoForm.video_url || ''}
                  onChange={e => setVideoForm({...videoForm, video_url: e.target.value})}
                  className="w-full p-2 border rounded-lg"
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'टाइटल (English) *' : 'Title (English) *'}</label>
                  <input 
                    value={videoForm.title || ''}
                    onChange={e => setVideoForm({...videoForm, title: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                    placeholder="Annual Day 2024"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'टाइटल (Hindi)' : 'Title (Hindi)'}</label>
                  <input 
                    value={videoForm.title_hindi || ''}
                    onChange={e => setVideoForm({...videoForm, title_hindi: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                    placeholder="वार्षिक दिवस 2024"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'विवरण' : 'Description'}</label>
                <textarea 
                  value={videoForm.description || ''}
                  onChange={e => setVideoForm({...videoForm, description: e.target.value})}
                  className="w-full p-2 border rounded-lg"
                  rows="2"
                  placeholder="Video description..."
                />
              </div>
              <div className="flex items-center gap-3">
                <input 
                  type="checkbox"
                  id="video_featured"
                  checked={videoForm.is_featured ?? false}
                  onChange={e => setVideoForm({...videoForm, is_featured: e.target.checked})}
                  className="w-5 h-5 text-orange-500"
                />
                <label htmlFor="video_featured" className="font-medium">⭐ {language === 'hi' ? 'Featured वीडियो' : 'Featured Video'}</label>
              </div>
              <button 
                onClick={() => saveGalleryVideo()}
                disabled={!videoForm.video_url || !videoForm.title}
                className="w-full py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 font-medium"
              >
                🎬 {language === 'hi' ? 'वीडियो जोड़ें' : 'Add Video'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Album Modal */}
      {showAlbumModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">{language === 'hi' ? '📁 नया एल्बम बनाएं' : '📁 Create New Album'}</h3>
              <button onClick={() => { setShowAlbumModal(false); setAlbumForm({}); }} className="text-2xl text-gray-500 hover:text-gray-700">×</button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'नाम (English) *' : 'Name (English) *'}</label>
                  <input 
                    value={albumForm.name || ''}
                    onChange={e => setAlbumForm({...albumForm, name: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                    placeholder="Annual Day 2024"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'नाम (Hindi)' : 'Name (Hindi)'}</label>
                  <input 
                    value={albumForm.name_hindi || ''}
                    onChange={e => setAlbumForm({...albumForm, name_hindi: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                    placeholder="वार्षिक दिवस 2024"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'एल्बम टाइप' : 'Album Type'}</label>
                <select 
                  value={albumForm.album_type || 'custom'}
                  onChange={e => setAlbumForm({...albumForm, album_type: e.target.value})}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="custom">{language === 'hi' ? 'कस्टम' : 'Custom'}</option>
                  <option value="year">{language === 'hi' ? 'साल के हिसाब से' : 'Year-wise'}</option>
                  <option value="class">{language === 'hi' ? 'क्लास के हिसाब से' : 'Class-wise'}</option>
                  <option value="event">{language === 'hi' ? 'इवेंट' : 'Event'}</option>
                </select>
              </div>
              {albumForm.album_type === 'year' && (
                <div>
                  <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'साल' : 'Year'}</label>
                  <input 
                    type="number"
                    value={albumForm.year || new Date().getFullYear()}
                    onChange={e => setAlbumForm({...albumForm, year: parseInt(e.target.value)})}
                    className="w-full p-2 border rounded-lg"
                  />
                </div>
              )}
              {albumForm.album_type === 'class' && (
                <div>
                  <label className="block text-sm font-medium mb-1">{language === 'hi' ? 'क्लास' : 'Class'}</label>
                  <input 
                    value={albumForm.class_name || ''}
                    onChange={e => setAlbumForm({...albumForm, class_name: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                    placeholder="Class 10"
                  />
                </div>
              )}
              <button 
                onClick={() => createAlbum()}
                disabled={!albumForm.name}
                className="w-full py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 font-medium"
              >
                📁 {language === 'hi' ? 'एल्बम बनाएं' : 'Create Album'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CTA Buttons Tab */}
      {activeTab === 'cta' && (
        <div className="space-y-6" data-testid="cta-tab">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-semibold text-lg">{language === 'hi' ? '📢 कॉल टू एक्शन बटन' : '📢 Call to Action Buttons'}</h3>
              <p className="text-sm text-gray-500">{language === 'hi' ? 'ये बटन स्कूल पेज पर दिखेंगे - Admission, Call, WhatsApp आदि' : 'These buttons will appear on your school page - Admission, Call, WhatsApp etc.'}</p>
            </div>
            <button onClick={addNewCTA} className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
              + {language === 'hi' ? 'नया बटन' : 'Add Button'}
            </button>
          </div>

          {/* CTA List */}
          <div className="space-y-4">
            {callToActions.map((cta, idx) => (
              <div key={cta.cta_id} className="bg-white rounded-xl shadow p-4">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-center">
                  {/* Icon Selector */}
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">{language === 'hi' ? 'आइकन' : 'Icon'}</label>
                    <select value={cta.icon || '🔗'} onChange={e => updateCTA(cta.cta_id, 'icon', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-lg">
                      {['📝', '📞', '💬', '📧', '🏫', '🔗', '📲', '💰', '📋', '🎓'].map(icon => (
                        <option key={icon} value={icon}>{icon}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Label */}
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">{language === 'hi' ? 'बटन टेक्स्ट' : 'Button Text'}</label>
                    <input value={cta.label || ''} onChange={e => updateCTA(cta.cta_id, 'label', e.target.value)}
                      placeholder="Call Now" className="w-full px-3 py-2 border rounded-lg text-sm" />
                  </div>
                  
                  {/* Hindi Label */}
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">{language === 'hi' ? 'हिंदी टेक्स्ट' : 'Hindi Text'}</label>
                    <input value={cta.label_hindi || ''} onChange={e => updateCTA(cta.cta_id, 'label_hindi', e.target.value)}
                      placeholder="अभी कॉल करें" className="w-full px-3 py-2 border rounded-lg text-sm" />
                  </div>
                  
                  {/* Action Type */}
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">{language === 'hi' ? 'टाइप' : 'Type'}</label>
                    <select value={cta.action_type || 'link'} onChange={e => updateCTA(cta.cta_id, 'action_type', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm">
                      <option value="phone">📞 Phone Call</option>
                      <option value="whatsapp">💬 WhatsApp</option>
                      <option value="email">📧 Email</option>
                      <option value="link">🔗 Link/URL</option>
                      <option value="admission_form">📝 Admission Form</option>
                    </select>
                  </div>
                  
                  {/* Action Value */}
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">{language === 'hi' ? 'नंबर/URL' : 'Number/URL'}</label>
                    <input value={cta.action_value || ''} onChange={e => updateCTA(cta.cta_id, 'action_value', e.target.value)}
                      placeholder={cta.action_type === 'phone' ? '9876543210' : cta.action_type === 'email' ? 'school@email.com' : 'https://...'}
                      className="w-full px-3 py-2 border rounded-lg text-sm" />
                  </div>
                  
                  {/* Toggles */}
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={cta.is_visible} onChange={e => updateCTA(cta.cta_id, 'is_visible', e.target.checked)} className="rounded" />
                      <span className="text-xs">{language === 'hi' ? 'दिखाएं' : 'Show'}</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={cta.is_primary} onChange={e => updateCTA(cta.cta_id, 'is_primary', e.target.checked)} className="rounded" />
                      <span className="text-xs">{language === 'hi' ? 'मुख्य' : 'Primary'}</span>
                    </label>
                    <button onClick={() => setCallToActions(prev => prev.filter(c => c.cta_id !== cta.cta_id))}
                      className="p-1 text-red-500 hover:bg-red-50 rounded">🗑️</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Preview */}
          <div className="bg-gray-100 rounded-xl p-6">
            <p className="text-sm font-medium text-gray-700 mb-4">{language === 'hi' ? '👁️ प्रीव्यू - ऐसे दिखेंगे बटन' : '👁️ Preview - Buttons will look like this'}</p>
            <div className="flex flex-wrap gap-3">
              {callToActions.filter(c => c.is_visible).map(cta => (
                <button key={cta.cta_id} className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${cta.is_primary ? 'bg-orange-500 text-white' : 'bg-white border border-gray-300 text-gray-700'}`}>
                  <span>{cta.icon}</span>
                  <span>{language === 'hi' ? cta.label_hindi || cta.label : cta.label}</span>
                </button>
              ))}
            </div>
          </div>

          <button onClick={saveCTAs} disabled={saving} className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50">
            {saving ? '...' : (language === 'hi' ? '💾 CTA बटन सेव करें' : '💾 Save CTA Buttons')}
          </button>
        </div>
      )}

      {/* School Profile Preview Tab */}
      {activeTab === 'preview' && (
        <div className="space-y-6" data-testid="preview-tab">
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex justify-between items-center">
            <div>
              <h3 className="font-semibold text-orange-700">{language === 'hi' ? '👁️ आपके स्कूल का पब्लिक प्रोफाइल पेज' : '👁️ Your School Public Profile Page'}</h3>
              <p className="text-sm text-orange-600">{language === 'hi' ? 'ऐसा दिखेगा आपका स्कूल पेज parents और visitors को' : 'This is how your school page will look to parents and visitors'}</p>
              {schoolSubdomain && (
                <p className="text-xs text-gray-500 mt-1">
                  🔗 URL: <code className="bg-white px-2 py-0.5 rounded">/school/{schoolSubdomain}</code>
                </p>
              )}
            </div>
            {schoolSubdomain ? (
              <a href={`/school/${schoolSubdomain}`} target="_blank" rel="noopener noreferrer"
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2">
                🌐 {language === 'hi' ? 'पब्लिक पेज खोलें' : 'Open Public Page'}
              </a>
            ) : (
              <span className="px-4 py-2 bg-gray-300 text-gray-600 rounded-lg cursor-not-allowed">
                {language === 'hi' ? 'Subdomain नहीं मिला' : 'No subdomain found'}
              </span>
            )}
          </div>

          {/* Profile Preview Card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden" style={{ borderTop: `4px solid ${branding?.primary_color || '#FF6B35'}` }}>
            {/* Banner */}
            <div className="h-48 bg-gradient-to-r from-orange-500 to-orange-600 relative" style={{ background: profile?.banner_url ? `url(${profile.banner_url}) center/cover` : `linear-gradient(135deg, ${branding?.primary_color || '#FF6B35'}, ${branding?.secondary_color || '#F7931E'})` }}>
              <div className="absolute inset-0 bg-black/30"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h1 className="text-3xl font-bold">{language === 'hi' && basicInfo?.name_hindi ? basicInfo.name_hindi : basicInfo?.name || 'School Name'}</h1>
                {basicInfo?.tagline && <p className="text-lg opacity-90 mt-1">{language === 'hi' && basicInfo?.tagline_hindi ? basicInfo.tagline_hindi : basicInfo.tagline}</p>}
              </div>
              {/* Logo */}
              <div className="absolute top-4 right-4 w-20 h-20 bg-white rounded-xl shadow-lg flex items-center justify-center overflow-hidden">
                {profile?.logo_url ? (
                  <img src={profile.logo_url} alt="Logo" className="w-full h-full object-contain p-1" />
                ) : (
                  <span className="text-3xl">🏫</span>
                )}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="p-4 bg-gray-50 border-b flex flex-wrap gap-3 justify-center">
              {callToActions.filter(c => c.is_visible).map(cta => (
                <button key={cta.cta_id} className={`px-5 py-2.5 rounded-lg font-medium flex items-center gap-2 transition ${cta.is_primary ? 'text-white shadow-lg hover:shadow-xl' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                  style={cta.is_primary ? { backgroundColor: branding?.primary_color || '#FF6B35' } : {}}>
                  <span>{cta.icon}</span>
                  <span>{language === 'hi' ? cta.label_hindi || cta.label : cta.label}</span>
                </button>
              ))}
            </div>

            {/* Info Grid */}
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* About */}
              <div className="md:col-span-2 space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2" style={{ color: branding?.primary_color || '#FF6B35' }}>{language === 'hi' ? 'स्कूल के बारे में' : 'About School'}</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {basicInfo?.established_year && <div><span className="text-gray-500">🗓️ स्थापना:</span> <strong>{basicInfo.established_year}</strong></div>}
                  {basicInfo?.affiliation_board && <div><span className="text-gray-500">📜 बोर्ड:</span> <strong>{basicInfo.affiliation_board}</strong></div>}
                  {basicInfo?.affiliation_number && <div><span className="text-gray-500">📋 संबद्धता:</span> <strong>{basicInfo.affiliation_number}</strong></div>}
                  {basicInfo?.medium && <div><span className="text-gray-500">📚 माध्यम:</span> <strong>{basicInfo.medium}</strong></div>}
                </div>
              </div>

              {/* Contact */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2" style={{ color: branding?.primary_color || '#FF6B35' }}>{language === 'hi' ? 'संपर्क' : 'Contact'}</h3>
                <div className="space-y-2 text-sm">
                  {contactInfo?.address && <p>📍 {language === 'hi' && contactInfo?.address_hindi ? contactInfo.address_hindi : contactInfo.address}</p>}
                  {contactInfo?.city && <p>{contactInfo.city}, {contactInfo?.state} - {contactInfo?.pincode}</p>}
                  {contactInfo?.phone && <p>📞 {contactInfo.phone}</p>}
                  {contactInfo?.email && <p>📧 {contactInfo.email}</p>}
                </div>
              </div>
            </div>

            {/* Team Section */}
            {teamMembers.length > 0 && (
              <div className="p-6 bg-gray-50">
                <h3 className="font-semibold text-lg mb-4" style={{ color: branding?.primary_color || '#FF6B35' }}>{language === 'hi' ? 'हमारी टीम' : 'Our Team'}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {teamMembers.slice(0, 4).map((member, idx) => (
                    <div key={idx} className="bg-white rounded-xl p-4 text-center shadow-sm">
                      <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center overflow-hidden mb-3">
                        {member.photo_url ? <img src={member.photo_url} alt={member.name} className="w-full h-full object-cover" /> : <span className="text-2xl">👤</span>}
                      </div>
                      <h4 className="font-medium text-gray-800 text-sm">{language === 'hi' && member.name_hindi ? member.name_hindi : member.name}</h4>
                      <p className="text-xs" style={{ color: branding?.primary_color || '#FF6B35' }}>{language === 'hi' && member.role_hindi ? member.role_hindi : member.role}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Social Links */}
            {(socialLinks?.facebook || socialLinks?.instagram || socialLinks?.youtube || socialLinks?.whatsapp) && (
              <div className="p-4 border-t flex justify-center gap-4">
                {socialLinks?.facebook && <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center hover:bg-blue-200">f</a>}
                {socialLinks?.instagram && <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center hover:bg-pink-200">📷</a>}
                {socialLinks?.youtube && <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-red-100 text-red-600 rounded-full flex items-center justify-center hover:bg-red-200">▶️</a>}
                {socialLinks?.whatsapp && <a href={`https://wa.me/${socialLinks.whatsapp}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center hover:bg-green-200">💬</a>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Home Page Editor Tab */}
      {activeTab === 'homepage' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold text-lg mb-4">{language === 'hi' ? '📄 होम पेज सेक्शन' : '📄 Home Page Sections'}</h3>
            <p className="text-sm text-gray-500 mb-4">{language === 'hi' ? 'सेक्शन को on/off करें और content edit करें।' : 'Toggle sections on/off and edit their content.'}</p>
            
            <div className="space-y-4">
              {homeSections.map(section => (
                <div key={section.section_id} className={`p-4 rounded-lg border ${section.is_visible ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">
                        {section.section_type === 'hero' ? '🏠' : 
                         section.section_type === 'about' ? '📖' :
                         section.section_type === 'features' ? '✨' :
                         section.section_type === 'gallery' ? '🖼️' :
                         section.section_type === 'announcements' ? '📢' :
                         section.section_type === 'contact' ? '📞' : '📄'}
                      </span>
                      <div>
                        <h4 className="font-medium">{section.title || section.section_type}</h4>
                        <p className="text-xs text-gray-500">{section.section_type}</p>
                      </div>
                    </div>
                    <button onClick={() => toggleSection(section.section_id)}
                      className={`px-3 py-1 rounded-full text-sm font-medium ${section.is_visible ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'}`}>
                      {section.is_visible ? 'ON' : 'OFF'}
                    </button>
                  </div>
                  
                  {section.is_visible && (
                    <div className="space-y-3 mt-3 pt-3 border-t">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">{language === 'hi' ? 'शीर्षक (English)' : 'Title (English)'}</label>
                          <input value={section.title || ''} onChange={e => updateSectionContent(section.section_id, 'title', e.target.value)} className="w-full px-3 py-2 border rounded text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">{language === 'hi' ? 'शीर्षक (हिंदी)' : 'Title (Hindi)'}</label>
                          <input value={section.title_hindi || ''} onChange={e => updateSectionContent(section.section_id, 'title_hindi', e.target.value)} className="w-full px-3 py-2 border rounded text-sm" />
                        </div>
                      </div>
                      {(section.section_type === 'hero' || section.section_type === 'about') && (
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">{language === 'hi' ? 'विवरण' : 'Content'}</label>
                          <textarea value={section.content || ''} onChange={e => updateSectionContent(section.section_id, 'content', e.target.value)} className="w-full px-3 py-2 border rounded text-sm h-20" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <button onClick={saveHomeSections} disabled={saving} className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 font-medium">
            {saving ? '...' : (language === 'hi' ? '💾 होम पेज सेव करें' : '💾 Save Home Page')}
          </button>
        </div>
      )}

      {/* Plan & Usage Tab */}
      {activeTab === 'plan' && (
        <div className="space-y-6" data-testid="plan-usage-tab">
          {/* Unified FREE Plan Card */}
          <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-green-500 rounded-2xl shadow-xl p-8 text-white" data-testid="free-plan-card">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <span className="inline-block bg-white/20 backdrop-blur px-3 py-1 rounded-full text-xs font-bold mb-2">
                  {language === 'hi' ? 'आपका Plan' : 'Your Plan'}
                </span>
                <h3 className="text-4xl font-black mb-1">🎓 {language === 'hi' ? 'FREE + Setup' : 'FREE + Setup'}</h3>
                <p className="text-white/90 text-sm max-w-xl">
                  {language === 'hi' 
                    ? 'Software Lifetime FREE + सभी ERP Modules + सभी 31 AI Features। AI आपकी खुद की Gemini API Key से चलेगा।' 
                    : 'Software Lifetime FREE + All ERP Modules + All 31 AI Features. AI runs on your own Gemini API Key.'}
                </p>
              </div>
              <div className="text-right bg-white/15 backdrop-blur rounded-xl p-4 border border-white/20">
                <p className="text-white/80 text-xs">{language === 'hi' ? 'One-time' : 'One-time'}</p>
                <p className="text-2xl font-black">₹4999</p>
                <p className="text-white/80 text-xs">{language === 'hi' ? '+ ₹999/साल maintenance' : '+ ₹999/year maintenance'}</p>
              </div>
            </div>
          </div>

          {/* AI Usage Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow p-5" data-testid="usage-total-calls">
              <p className="text-gray-500 text-sm">{language === 'hi' ? 'कुल AI कॉल्स (30 दिन)' : 'Total AI Calls (30 days)'}</p>
              <p className="text-3xl font-bold text-gray-800">{usageStats?.summary?.total_calls || 0}</p>
              <p className="text-sm text-gray-400">{language === 'hi' ? 'औसत: ' : 'Avg: '}{usageStats?.summary?.avg_calls_per_day || 0}/day</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5" data-testid="usage-total-cost">
              <p className="text-gray-500 text-sm">{language === 'hi' ? 'AI लागत (Platform को)' : 'AI Cost (To Platform)'}</p>
              <p className="text-3xl font-bold text-green-600">₹0</p>
              <p className="text-sm text-gray-400">{language === 'hi' ? 'आपकी Gemini Key से' : 'On your Gemini Key'}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5" data-testid="usage-tier-distribution">
              <p className="text-gray-500 text-sm">{language === 'hi' ? 'Gemini Model उपयोग' : 'Gemini Model Usage'}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Flash-Lite: {usageStats?.by_tier?.[1]?.calls || 0}</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Flash: {usageStats?.by_tier?.[2]?.calls || 0}</span>
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">Pro: {usageStats?.by_tier?.[3]?.calls || 0}</span>
              </div>
            </div>
          </div>

          {/* What's Included */}
          <div className="bg-white rounded-xl shadow p-6" data-testid="plan-includes">
            <h3 className="font-bold text-lg text-gray-800 mb-4">
              ✅ {language === 'hi' ? 'आपके Plan में क्या-क्या शामिल है' : "What's Included in Your Plan"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { hi: '8 ERP Modules (Attendance, Fees, Exams, Timetable, Transport, HR, Library, Calendar)', en: '8 ERP Modules (Attendance, Fees, Exams, Timetable, Transport, HR, Library, Calendar)' },
                { hi: '31 AI Features (Flash-Lite / Flash / Pro tiered routing)', en: '31 AI Features (Flash-Lite / Flash / Pro tiered routing)' },
                { hi: 'Admission Engine — 8 AI Tools', en: 'Admission Engine — 8 AI Tools' },
                { hi: 'Campaign Manager (Social Media + Ads + Messaging)', en: 'Campaign Manager (Social Media + Ads + Messaging)' },
                { hi: 'Online Fee Collection (UPI, Card, NetBanking)', en: 'Online Fee Collection (UPI, Card, NetBanking)' },
                { hi: 'Parent App + Student App', en: 'Parent App + Student App' },
                { hi: 'Aayush AI Chatbot (Lead Capture)', en: 'Aayush AI Chatbot (Lead Capture)' },
                { hi: 'Priority Support + 30 दिन Handholding', en: 'Priority Support + 30-Day Handholding' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-green-500 font-bold mt-0.5">✓</span>
                  <span>{language === 'hi' ? item.hi : item.en}</span>
                </div>
              ))}
            </div>
          </div>

          {/* AI Model Tier Info */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold text-gray-800 mb-4">🧠 {language === 'hi' ? 'AI Model Tiers (Gemini)' : 'AI Model Tiers (Gemini)'}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-1 bg-green-500 text-white text-xs rounded font-bold">Tier 1</span>
                  <span className="font-medium text-green-700">Gemini Flash-Lite</span>
                </div>
                <p className="text-sm text-green-600">{language === 'hi' ? 'तेज़ और सस्ता — Reminders, Alerts, Templates' : 'Fast and cheap — Reminders, Alerts, Templates'}</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded font-bold">Tier 2</span>
                  <span className="font-medium text-blue-700">Gemini Flash</span>
                </div>
                <p className="text-sm text-blue-600">{language === 'hi' ? 'Balanced — MCQ, Report Cards, Notices' : 'Balanced — MCQ, Report Cards, Notices'}</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-1 bg-purple-500 text-white text-xs rounded font-bold">Tier 3</span>
                  <span className="font-medium text-purple-700">Gemini Pro</span>
                </div>
                <p className="text-sm text-purple-600">{language === 'hi' ? 'सर्वश्रेष्ठ — At-Risk Alerts, Question Papers' : 'Best — At-Risk Alerts, Question Papers'}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-4 text-center">
              💡 {language === 'hi' 
                ? 'AI कॉल्स आपकी खुद की Gemini API Key से होते हैं — Google AI Studio से FREE key लें।' 
                : 'AI calls run on your own Gemini API Key — get a FREE key from Google AI Studio.'}
            </p>
          </div>
        </div>
      )}

      {/* Integrations Tab */}
      {activeTab === 'integrations' && (
        <div className="space-y-6">
          <p className="text-gray-600" data-testid="integrations-intro-copy">
            {isSchoolAdmin
              ? (language === 'hi'
                  ? 'यहाँ अपना Meta Connection (Facebook Ads + WhatsApp), Gemini AI API Key, Email SMTP और Razorpay रखें। Meta connect करने पर ads और WhatsApp आपके ही account से चलेंगे।'
                  : 'Manage your Meta Connection (Facebook Ads + WhatsApp), Gemini AI API Key, Email SMTP and Razorpay here. Once Meta is connected, ads & WhatsApp run from your own account.')
              : (language === 'hi'
                  ? 'Platform Admin यहाँ WhatsApp Business API, webhook/template controls और social media APIs manage करेगा। School-owned billing और AI keys School Admin settings में रहेंगी।'
                  : 'Platform Admin manages WhatsApp Business API, webhook/template controls, and social media APIs here. School-owned billing and AI keys stay inside School Admin settings.')}
          </p>

          {/* ====== META CONNECTION (Facebook Ads + WhatsApp) — school's own account ====== */}
          {schoolId && isSchoolAdmin && (
          <div className="bg-white rounded-xl shadow p-6 border-2 border-blue-100" data-testid="meta-connection-section">
            <div className="flex justify-between items-start mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-green-100 rounded-xl flex items-center justify-center text-2xl">📘</div>
                <div>
                  <h3 className="font-semibold text-lg">{language === 'hi' ? 'Meta Connection (Facebook Ads + WhatsApp)' : 'Meta Connection (Facebook Ads + WhatsApp)'}</h3>
                  <p className="text-sm text-gray-500">{language === 'hi' ? 'अपना खुद का Meta account जोड़ें — ads और WhatsApp आपके ही account से चलेंगे' : 'Connect your own Meta account — ads & WhatsApp will run from your account'}</p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <button onClick={() => setShowMetaGuide(true)} data-testid="open-meta-guide-btn"
                  className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 whitespace-nowrap">
                  📘 {language === 'hi' ? 'पहली बार? पूरी Step-by-Step Guide' : 'First time? Full Step-by-Step Guide'}
                </button>
                <div className="flex gap-2">
                  <span data-testid="meta-fb-status" className={`px-3 py-1 rounded-full text-xs font-medium ${integrations?.meta?.fb_configured ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    Ads: {integrations?.meta?.fb_configured ? 'Connected' : 'Not set'}
                  </span>
                  <span data-testid="meta-wa-status" className={`px-3 py-1 rounded-full text-xs font-medium ${integrations?.meta?.wa_configured ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    WhatsApp: {integrations?.meta?.wa_configured ? 'Connected' : 'Not set'}
                  </span>
                </div>
              </div>
            </div>

            <MetaSetupGuide open={showMetaGuide} onClose={() => setShowMetaGuide(false)} />

            <details className="mb-4 rounded-lg border border-blue-100 bg-blue-50/40" data-testid="meta-setup-guide">
              <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-blue-800">
                {language === 'hi' ? '⏱️ 2 मिनट में Setup — कैसे करें? (खोलें)' : '⏱️ 2-minute Setup — How to (open)'}
              </summary>
              <div className="px-5 pb-4 pt-1 text-sm text-gray-700 space-y-3">
                <div>
                  <p className="font-medium text-blue-800">{language === 'hi' ? 'चरण 1 — Facebook Ad Account ID' : 'Step 1 — Facebook Ad Account ID'}</p>
                  <p className="text-gray-600">{language === 'hi' ? 'Meta Business Settings → Accounts → Ad accounts खोलें, अपना Ad Account चुनें और ऊपर दिखने वाला नंबर (act_ के बाद वाला) कॉपी करें।' : 'Open Meta Business Settings → Accounts → Ad accounts, pick your ad account and copy the number shown (after act_).'}</p>
                  <a href="https://business.facebook.com/settings/ad-accounts" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">{language === 'hi' ? 'Ad Accounts खोलें ↗' : 'Open Ad Accounts ↗'}</a>
                </div>
                <div>
                  <p className="font-medium text-blue-800">{language === 'hi' ? 'चरण 2 — Page ID + System User Token' : 'Step 2 — Page ID + System User Token'}</p>
                  <p className="text-gray-600">{language === 'hi' ? 'Business Settings → Users → System Users में एक System User बनाएँ, उसे अपने Ad Account व Page का access दें, फिर "Generate Token" से token बनाएँ (ads_management, business_management permissions चुनें)।' : 'In Business Settings → Users → System Users, create a System User, give it access to your Ad Account & Page, then Generate Token (select ads_management, business_management).'}</p>
                  <a href="https://business.facebook.com/settings/system-users" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">{language === 'hi' ? 'System Users खोलें ↗' : 'Open System Users ↗'}</a>
                </div>
                <div>
                  <p className="font-medium text-green-800">{language === 'hi' ? 'चरण 3 — WhatsApp Phone Number ID + Token' : 'Step 3 — WhatsApp Phone Number ID + Token'}</p>
                  <p className="text-gray-600">{language === 'hi' ? 'developers.facebook.com → आपका App → WhatsApp → API Setup में Phone Number ID और access token मिलेंगे। (Production के लिए permanent token इस्तेमाल करें।)' : 'developers.facebook.com → your App → WhatsApp → API Setup gives the Phone Number ID and access token. (Use a permanent token for production.)'}</p>
                  <a href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline text-xs">{language === 'hi' ? 'WhatsApp API Setup गाइड ↗' : 'WhatsApp API Setup guide ↗'}</a>
                </div>
                <p className="text-xs text-gray-500 pt-1 border-t border-blue-100">{language === 'hi' ? 'नीचे fields भरें → "Meta Connection सेव करें" → फिर "Test Connection" से जाँचें। ✅' : 'Fill the fields below → "Save Meta Connection" → then verify with "Test Connection". ✅'}</p>
              </div>
            </details>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Facebook Ads */}
              <div className="space-y-3 p-4 rounded-lg bg-blue-50/50 border border-blue-100">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-blue-800 text-sm">📘 Facebook Ads</h4>
                  <a href="https://business.facebook.com/settings/ad-accounts" target="_blank" rel="noopener noreferrer" data-testid="meta-fb-help" className="text-xs text-blue-600 hover:underline">{language === 'hi' ? 'कहाँ से मिलेगा? ↗' : 'Where to find? ↗'}</a>
                </div>
                <input data-testid="meta-fb-ad-account" type="text" placeholder={integrations?.meta?.fb_ad_account_id ? `Saved: ${integrations.meta.fb_ad_account_id}` : 'Ad Account ID (e.g. 1234567890)'} value={metaForm.fb_ad_account_id} onChange={e => setMetaForm({ ...metaForm, fb_ad_account_id: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <input data-testid="meta-fb-page" type="text" placeholder={integrations?.meta?.fb_page_id ? `Saved: ${integrations.meta.fb_page_id}` : 'Facebook Page ID'} value={metaForm.fb_page_id} onChange={e => setMetaForm({ ...metaForm, fb_page_id: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <input data-testid="meta-fb-token" type="password" placeholder={integrations?.meta?.fb_configured ? `Saved: ${integrations.meta.fb_access_token_masked}` : 'System User Access Token'} value={metaForm.fb_access_token} onChange={e => setMetaForm({ ...metaForm, fb_access_token: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <p className="text-[11px] text-gray-500">{language === 'hi' ? '💡 WhatsApp वाला ही System User Token है? तो इसे खाली छोड़ दें — वही token अपने-आप इस्तेमाल होगा।' : '💡 Same System User Token as WhatsApp? Leave this empty — it will reuse that token automatically.'}</p>
                <a href="https://business.facebook.com/settings/system-users" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline block">{language === 'hi' ? 'System User Token बनाएँ ↗' : 'Create System User Token ↗'}</a>
                {integrations?.meta?.fb_configured && (
                  <button data-testid="meta-fb-remove" onClick={() => removeMeta('fb')} className="text-xs text-red-600 hover:underline">Remove Facebook Ads</button>
                )}
              </div>

              {/* WhatsApp */}
              <div className="space-y-3 p-4 rounded-lg bg-green-50/50 border border-green-100">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-green-800 text-sm">💬 WhatsApp Business</h4>
                  <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" data-testid="meta-wa-help" className="text-xs text-green-600 hover:underline">{language === 'hi' ? 'कहाँ से मिलेगा? ↗' : 'Where to find? ↗'}</a>
                </div>
                <input data-testid="meta-wa-pnid" type="text" placeholder={integrations?.meta?.wa_phone_number_id ? `Saved: ${integrations.meta.wa_phone_number_id}` : 'Phone Number ID'} value={metaForm.wa_phone_number_id} onChange={e => setMetaForm({ ...metaForm, wa_phone_number_id: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <input data-testid="meta-wa-waba" type="text" placeholder={integrations?.meta?.wa_business_account_id ? `Saved: ${integrations.meta.wa_business_account_id}` : 'WhatsApp Business Account ID (optional)'} value={metaForm.wa_business_account_id} onChange={e => setMetaForm({ ...metaForm, wa_business_account_id: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <input data-testid="meta-wa-token" type="password" placeholder={integrations?.meta?.wa_configured ? `Saved: ${integrations.meta.wa_access_token_masked}` : 'WhatsApp Access Token'} value={metaForm.wa_access_token} onChange={e => setMetaForm({ ...metaForm, wa_access_token: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <a href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started" target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:underline block">{language === 'hi' ? 'WhatsApp API Setup गाइड ↗' : 'WhatsApp API Setup guide ↗'}</a>
                {integrations?.meta?.wa_configured && (
                  <button data-testid="meta-wa-remove" onClick={() => removeMeta('wa')} className="text-xs text-red-600 hover:underline">Remove WhatsApp</button>
                )}
              </div>
            </div>

            {metaTest && (
              <div className="mt-4 space-y-2" data-testid="meta-test-result">
                <div className={`text-sm rounded-lg px-3 py-2 ${metaTest.fb.ok ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>📘 Facebook Ads: {metaTest.fb.detail}</div>
                <div className={`text-sm rounded-lg px-3 py-2 ${metaTest.wa.ok ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>💬 WhatsApp: {metaTest.wa.detail}</div>
              </div>
            )}

            <div className="flex gap-3 mt-4">
              <button data-testid="meta-save-btn" onClick={saveMeta} disabled={metaSaving} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg disabled:opacity-50">
                {metaSaving ? (language === 'hi' ? 'सेव हो रहा है...' : 'Saving...') : (language === 'hi' ? 'Meta Connection सेव करें' : 'Save Meta Connection')}
              </button>
              <button data-testid="meta-test-btn" onClick={testMeta} disabled={metaTesting} className="px-5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2.5 rounded-lg disabled:opacity-50 border border-gray-300">
                {metaTesting ? (language === 'hi' ? 'जाँच रहे हैं...' : 'Testing...') : (language === 'hi' ? 'Test Connection' : 'Test Connection')}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2">{language === 'hi' ? 'भरे हुए fields ही सेव होंगे। पहले सेव करें, फिर Test Connection से जाँचें।' : 'Only filled fields are saved. Save first, then verify with Test Connection.'}</p>
          </div>
          )}

          {/* ====== GEMINI AI API KEY (School's own key → FREE AI features) ====== */}
          {schoolId && isSchoolAdmin && (
          <div className="bg-white rounded-xl shadow p-6 border-2 border-purple-100" data-testid="gemini-key-section">
            <div className="flex justify-between items-start mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-xl flex items-center justify-center">
                  <svg className="w-7 h-7 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{language === 'hi' ? 'Gemini AI API Key' : 'Gemini AI API Key'}</h3>
                  <p className="text-sm text-gray-500">{language === 'hi' ? 'अपनी खुद की Google Gemini API Key डालें — सारे AI features 100% FREE' : 'Use your own Google Gemini API Key — all AI features 100% FREE'}</p>
                </div>
              </div>
              <span data-testid="gemini-status-badge" className={`px-3 py-1 rounded-full text-sm font-medium ${geminiKeyStatus.has_key ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {geminiKeyStatus.has_key ? (language === 'hi' ? 'Connected' : 'Connected') : (language === 'hi' ? 'Not Configured' : 'Not Configured')}
              </span>
            </div>

            {geminiKeyStatus.has_key && !showGeminiForm ? (
              <div className="space-y-3">
                <div className="p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg border border-purple-100">
                  <div className="text-sm">
                    <span className="text-gray-500">{language === 'hi' ? 'API Key:' : 'API Key:'}</span>
                    <span className="ml-2 font-mono text-purple-700" data-testid="gemini-key-preview">{geminiKeyStatus.key_preview}</span>
                  </div>
                </div>
                {geminiTestResult && (
                  <div className={`p-3 rounded-lg text-sm ${geminiTestResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="gemini-test-result">
                    {geminiTestResult.success ? '✅' : '❌'} {geminiTestResult.message}
                  </div>
                )}
                <div className="flex gap-2 flex-wrap">
                  <button onClick={testGeminiKey} disabled={geminiTesting} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50" data-testid="btn-test-gemini">
                    {geminiTesting ? (language === 'hi' ? 'Testing...' : 'Testing...') : (language === 'hi' ? 'Test Connection' : 'Test Connection')}
                  </button>
                  <button onClick={() => { setGeminiKeyInput(''); setShowGeminiForm(true); setGeminiTestResult(null); }} className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200" data-testid="btn-edit-gemini">
                    {language === 'hi' ? 'Update Key' : 'Update Key'}
                  </button>
                  <button onClick={removeGeminiKey} className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200" data-testid="btn-remove-gemini">
                    {language === 'hi' ? 'Remove' : 'Remove'}
                  </button>
                </div>
              </div>
            ) : !showGeminiForm ? (
              <button onClick={() => setShowGeminiForm(true)} className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 font-medium shadow-sm" data-testid="btn-configure-gemini">
                + {language === 'hi' ? 'Gemini API Key जोड़ें' : 'Add Gemini API Key'}
              </button>
            ) : null}

            {showGeminiForm && (
              <div className="space-y-4 p-5 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl mt-4 border border-purple-200" data-testid="gemini-form">
                <h4 className="font-semibold text-purple-900">{language === 'hi' ? 'Gemini API Key डालें' : 'Enter Gemini API Key'}</h4>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Google Gemini API Key *</label>
                  <input
                    type="password"
                    value={geminiKeyInput}
                    onChange={e => setGeminiKeyInput(e.target.value)}
                    placeholder="AIza..."
                    className="w-full px-4 py-2.5 border border-purple-300 rounded-lg focus:border-purple-500 focus:outline-none font-mono text-sm"
                    data-testid="input-gemini-key"
                    autoComplete="off"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {language === 'hi' ? 'Save करते ही Key verify होगी (test API call)' : 'Key will be verified on save (test API call)'}
                  </p>
                </div>

                <div className="flex gap-2 pt-2">
                  <button onClick={saveGeminiKey} disabled={geminiSaving} className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50" data-testid="btn-save-gemini">
                    {geminiSaving ? (language === 'hi' ? 'Verifying...' : 'Verifying...') : (language === 'hi' ? 'Verify & Save' : 'Verify & Save')}
                  </button>
                  <button onClick={() => { setShowGeminiForm(false); setGeminiKeyInput(''); setGeminiTestResult(null); }} className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Cancel</button>
                </div>

                <div className="bg-white rounded-lg p-3 text-sm text-purple-800 border border-purple-100">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <p className="font-medium mb-2">{language === 'hi' ? 'Gemini API Key कहाँ से लें?' : 'Where to get your Gemini API Key?'}</p>
                    <button
                      type="button"
                      onClick={() => setShowGeminiQR(!showGeminiQR)}
                      className="text-xs px-3 py-1 rounded-full bg-purple-100 text-purple-700 hover:bg-purple-200 font-medium flex items-center gap-1.5"
                      data-testid="btn-toggle-gemini-qr"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5zM13.5 14.25h1.5v1.5h-1.5v-1.5zM15 15.75h1.5v1.5H15v-1.5zM16.5 14.25H18v1.5h-1.5v-1.5zM18 15.75h1.5v1.5H18v-1.5zM19.5 14.25h1.5v1.5h-1.5v-1.5zM13.5 17.25h1.5v1.5h-1.5v-1.5zM16.5 17.25H18v1.5h-1.5v-1.5zM19.5 17.25h1.5v1.5h-1.5v-1.5z" />
                      </svg>
                      {showGeminiQR ? (language === 'hi' ? 'QR छिपाएँ' : 'Hide QR') : (language === 'hi' ? '📱 Mobile से खोलें (QR)' : '📱 Open on Mobile (QR)')}
                    </button>
                  </div>
                  <ol className="space-y-1 text-xs list-decimal pl-5">
                    <li>
                      <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-purple-600 underline hover:text-purple-800">
                        Google AI Studio (aistudio.google.com/app/apikey)
                      </a> {language === 'hi' ? 'पर जाएँ' : 'पर जाएँ'}
                    </li>
                    <li>{language === 'hi' ? 'अपनी Google ID से login करें' : 'Sign in with your Google account'}</li>
                    <li>{language === 'hi' ? '"Create API Key" पर click करें' : 'Click "Create API Key"'}</li>
                    <li>{language === 'hi' ? 'Key को copy करके यहाँ paste करें' : 'Copy the key and paste it here'}</li>
                  </ol>
                  <p className="mt-2 text-xs text-gray-600">{language === 'hi' ? '💡 Free tier पर पर्याप्त quota मिलता है छोटे/मध्यम स्कूलों के लिए।' : '💡 Free tier has sufficient quota for small/medium schools.'}</p>

                  {showGeminiQR && (
                    <div className="mt-4 pt-4 border-t border-purple-100 flex flex-col sm:flex-row items-center gap-4" data-testid="gemini-qr-box">
                      <div className="bg-white p-3 rounded-xl border-2 border-purple-200 shadow-sm flex-shrink-0">
                        <QRCodeSVG
                          value="https://aistudio.google.com/app/apikey"
                          size={144}
                          bgColor="#ffffff"
                          fgColor="#6d28d9"
                          level="M"
                          includeMargin={false}
                        />
                      </div>
                      <div className="text-xs text-purple-900 space-y-1.5">
                        <p className="font-semibold text-sm">📱 {language === 'hi' ? 'Quick Verify — Mobile Steps' : 'Quick Verify — Mobile Steps'}</p>
                        <ol className="list-decimal pl-4 space-y-1">
                          <li>{language === 'hi' ? 'अपने phone के camera से QR scan करें' : 'Scan QR with your phone camera'}</li>
                          <li>{language === 'hi' ? 'Google AI Studio खुलेगा — login करें' : 'Google AI Studio opens — sign in'}</li>
                          <li>{language === 'hi' ? '"Create API Key" tap करें' : 'Tap "Create API Key"'}</li>
                          <li>{language === 'hi' ? 'Key copy करके इस desktop पर paste करें' : 'Copy key and paste here on desktop'}</li>
                        </ol>
                        <p className="text-[11px] text-purple-600 font-mono break-all pt-1">aistudio.google.com/app/apikey</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          )}

          {/* ====== BYODB — School's Own Database ====== */}
          {schoolId && (
            <BYODBPanel token={token} schoolId={schoolId} language={language} />
          )}

          {/* ====== EMAIL SMTP ====== */}
          {isSchoolAdmin && (
          <div className="bg-white rounded-xl shadow p-6" data-testid="smtp-config-section">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-2xl">
                  <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Email SMTP</h3>
                  <p className="text-sm text-gray-500">{language === 'hi' ? 'Newsletter और emails भेजने के लिए SMTP setup करें। आपके school का अपना email address use होगा।' : 'Configure SMTP for sending newsletters and emails from your school email.'}</p>
                </div>
              </div>
              <span data-testid="smtp-status-badge" className={`px-3 py-1 rounded-full text-sm font-medium ${smtpConfig?.configured ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {smtpConfig?.configured ? (smtpConfig?.is_school_specific ? (language === 'hi' ? 'School SMTP Connected' : 'School SMTP Connected') : 'Connected') : (language === 'hi' ? 'Not Configured' : 'Not Configured')}
              </span>
            </div>

            {smtpConfig?.configured ? (
              <div className="space-y-3">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-gray-500">Host:</span> <span className="ml-2 font-mono">{smtpConfig.config?.smtp_host}</span></div>
                    <div><span className="text-gray-500">Port:</span> <span className="ml-2 font-mono">{smtpConfig.config?.smtp_port}</span></div>
                    <div><span className="text-gray-500">Email:</span> <span className="ml-2 font-medium">{smtpConfig.config?.smtp_email}</span></div>
                    <div><span className="text-gray-500">Password:</span> <span className="ml-2 font-mono">{smtpConfig.config?.smtp_password_masked}</span></div>
                    <div><span className="text-gray-500">From Name:</span> <span className="ml-2">{smtpConfig.config?.from_name}</span></div>
                    <div><span className="text-gray-500">SSL:</span> <span className="ml-2">{smtpConfig.config?.use_ssl ? 'Yes' : 'No'}</span></div>
                  </div>
                  {smtpConfig.config?.updated_at && (
                    <p className="text-xs text-gray-400 mt-2">{language === 'hi' ? 'अपडेट:' : 'Updated:'} {new Date(smtpConfig.config.updated_at).toLocaleString()}</p>
                  )}
                </div>
                {smtpTestResult && (
                  <div className={`p-3 rounded-lg text-sm ${smtpTestResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="smtp-test-result">
                    {smtpTestResult.success ? '✅' : '❌'} {smtpTestResult.message}
                  </div>
                )}
                <div className="flex gap-2 flex-wrap">
                  <button onClick={testSmtp} disabled={smtpTesting} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50" data-testid="btn-test-smtp">
                    {smtpTesting ? 'Testing...' : (language === 'hi' ? 'Test Connection' : 'Test Connection')}
                  </button>
                  <button onClick={() => { setSmtpForm({ smtp_host: smtpConfig.config?.smtp_host || 'smtpout.secureserver.net', smtp_port: smtpConfig.config?.smtp_port || 465, smtp_email: smtpConfig.config?.smtp_email || '', smtp_password: '', from_name: smtpConfig.config?.from_name || 'शिक्षा मित्र', use_ssl: smtpConfig.config?.use_ssl ?? true }); setShowSmtpForm(true); }} className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200" data-testid="btn-edit-smtp">
                    {language === 'hi' ? 'Edit' : 'Edit'}
                  </button>
                  <button onClick={removeSmtp} className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200" data-testid="btn-remove-smtp">Remove</button>
                </div>
                {/* Send Test Email */}
                <div className="flex gap-2 items-center mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100" data-testid="smtp-send-test-section">
                  <input 
                    type="email" placeholder={language === 'hi' ? 'Test email भेजें — recipient email' : 'Send test email to...'}
                    id="smtp-test-email-input"
                    className="flex-1 px-3 py-2 border rounded-lg text-sm focus:border-blue-400 outline-none"
                    data-testid="smtp-test-email-input"
                  />
                  <button 
                    onClick={async () => {
                      const emailInput = document.getElementById('smtp-test-email-input');
                      const email = emailInput?.value;
                      if (!email) return;
                      emailInput.disabled = true;
                      const res = await api.post('/api/platform-settings/smtp/send-test-email', { to_email: email }, token);
                      emailInput.disabled = false;
                      setSmtpTestResult(res);
                      setToast({ message: res.message, type: res.success ? 'success' : 'error' });
                    }}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm font-medium whitespace-nowrap"
                    data-testid="btn-send-test-email"
                  >
                    {language === 'hi' ? '📧 Test Email भेजें' : '📧 Send Test Email'}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {!showSmtpForm ? (
                  <button onClick={() => setShowSmtpForm(true)} className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium" data-testid="btn-configure-smtp">
                    + {language === 'hi' ? 'SMTP Configure करें' : 'Configure SMTP'}
                  </button>
                ) : null}
              </div>
            )}

            {showSmtpForm && (
              <div className="space-y-4 p-5 bg-slate-50 rounded-xl mt-4 border border-slate-200" data-testid="smtp-form">
                <h4 className="font-semibold text-slate-800">{language === 'hi' ? 'SMTP Credentials डालें' : 'Enter SMTP Credentials'}</h4>
                
                {/* Quick Presets */}
                <div className="flex gap-2 flex-wrap">
                  <span className="text-sm text-gray-500 py-1">{language === 'hi' ? 'Quick Setup:' : 'Quick Setup:'}</span>
                  {[
                    { label: 'GoDaddy', host: 'smtpout.secureserver.net', port: 465, ssl: true },
                    { label: 'Gmail', host: 'smtp.gmail.com', port: 465, ssl: true },
                    { label: 'Outlook', host: 'smtp.office365.com', port: 587, ssl: false },
                    { label: 'Zoho', host: 'smtp.zoho.in', port: 465, ssl: true },
                  ].map(preset => (
                    <button key={preset.label} onClick={() => setSmtpForm(prev => ({ ...prev, smtp_host: preset.host, smtp_port: preset.port, use_ssl: preset.ssl }))}
                      className="px-3 py-1 rounded-full text-xs bg-white border border-slate-300 text-slate-700 hover:border-orange-400 hover:text-orange-600 transition"
                      data-testid={`smtp-preset-${preset.label.toLowerCase()}`}
                    >{preset.label}</button>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Host *</label>
                    <input value={smtpForm.smtp_host} onChange={e => setSmtpForm({...smtpForm, smtp_host: e.target.value})} 
                      placeholder="smtpout.secureserver.net" className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:border-orange-500 focus:outline-none" data-testid="input-smtp-host" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Port *</label>
                    <input type="number" value={smtpForm.smtp_port} onChange={e => setSmtpForm({...smtpForm, smtp_port: parseInt(e.target.value) || 465})} 
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:border-orange-500 focus:outline-none" data-testid="input-smtp-port" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                    <input type="email" value={smtpForm.smtp_email} onChange={e => setSmtpForm({...smtpForm, smtp_email: e.target.value})} 
                      placeholder="info@yourschool.edu.in" className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:border-orange-500 focus:outline-none" data-testid="input-smtp-email" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                    <input type="password" value={smtpForm.smtp_password} onChange={e => setSmtpForm({...smtpForm, smtp_password: e.target.value})} 
                      placeholder="••••••••" className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:border-orange-500 focus:outline-none" data-testid="input-smtp-password" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From Name</label>
                    <input value={smtpForm.from_name} onChange={e => setSmtpForm({...smtpForm, from_name: e.target.value})} 
                      placeholder="शिक्षा मित्र" className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:border-orange-500 focus:outline-none" data-testid="input-smtp-from-name" />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer pb-2">
                      <input type="checkbox" checked={smtpForm.use_ssl} onChange={e => setSmtpForm({...smtpForm, use_ssl: e.target.checked})} className="w-4 h-4 accent-orange-500" data-testid="toggle-smtp-ssl" />
                      <span className="text-sm font-medium text-gray-700">Use SSL (Port 465)</span>
                    </label>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button onClick={saveSmtp} disabled={saving} className="px-6 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50" data-testid="btn-save-smtp">
                    {saving ? '...' : (language === 'hi' ? 'Save & Connect' : 'Save & Connect')}
                  </button>
                  <button onClick={() => { setShowSmtpForm(false); setSmtpTestResult(null); }} className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Cancel</button>
                </div>

                <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700">
                  <p className="font-medium mb-1">{language === 'hi' ? '💡 GoDaddy SMTP Setup:' : '💡 GoDaddy SMTP Setup:'}</p>
                  <ul className="space-y-1 text-xs">
                    <li>• Host: <code className="bg-blue-100 px-1 rounded">smtpout.secureserver.net</code>, Port: <code className="bg-blue-100 px-1 rounded">465</code> (SSL)</li>
                    <li>• Email: {language === 'hi' ? 'आपका GoDaddy email (e.g. info@yourschool.edu.in)' : 'Your GoDaddy email (e.g. info@yourschool.edu.in)'}</li>
                    <li>• Password: {language === 'hi' ? 'GoDaddy email का password' : 'Your GoDaddy email password'}</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
          )}

          {/* ====== WhatsApp Business API ====== */}
          {isPlatformAdmin && (
          <div className="bg-white rounded-xl shadow p-6" data-testid="whatsapp-config-section">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg">WhatsApp Business API</h3>
                  <p className="text-sm text-gray-500">{language === 'hi' ? 'Meta WhatsApp Business API से parents को messages भेजें' : 'Send messages to parents via Meta WhatsApp Business API'}</p>
                </div>
              </div>
              <span data-testid="whatsapp-status-badge" className={`px-3 py-1 rounded-full text-sm font-medium ${whatsappConfig?.configured ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {whatsappConfig?.configured ? 'Connected' : 'Not Configured'}
              </span>
            </div>

            {whatsappConfig?.configured ? (
              <div className="space-y-3">
                <div className="p-4 bg-gray-50 rounded-lg text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <div><span className="text-gray-500">Phone Number ID:</span> <span className="ml-1 font-mono">{whatsappConfig.config?.phone_number_id}</span></div>
                    <div><span className="text-gray-500">Business Account:</span> <span className="ml-1 font-mono">{whatsappConfig.config?.business_account_id}</span></div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setShowWhatsappForm(true); }} className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200">Edit</button>
                  <button onClick={removeWhatsappBusiness} className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">Remove</button>
                </div>
              </div>
            ) : (
              <div>
                {!showWhatsappForm ? (
                  <button onClick={() => setShowWhatsappForm(true)} className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium" data-testid="btn-configure-whatsapp">
                    + {language === 'hi' ? 'WhatsApp Configure करें' : 'Configure WhatsApp'}
                  </button>
                ) : null}
              </div>
            )}

            {showWhatsappForm && (
              <div className="space-y-4 p-5 bg-green-50 rounded-xl mt-4 border border-green-200" data-testid="whatsapp-form">
                <h4 className="font-semibold text-green-800">{language === 'hi' ? 'Meta WhatsApp Business API Credentials' : 'Meta WhatsApp Business API Credentials'}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number ID *</label>
                    <input value={whatsappForm.phone_number_id} onChange={e => setWhatsappForm({...whatsappForm, phone_number_id: e.target.value})} 
                      placeholder="e.g. 123456789" className="w-full px-4 py-2.5 border rounded-lg focus:border-green-500 focus:outline-none" data-testid="input-wa-phone-id" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business Account ID</label>
                    <input value={whatsappForm.business_account_id} onChange={e => setWhatsappForm({...whatsappForm, business_account_id: e.target.value})} 
                      placeholder="e.g. 987654321" className="w-full px-4 py-2.5 border rounded-lg focus:border-green-500 focus:outline-none" data-testid="input-wa-business-id" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Access Token *</label>
                    <input type="password" value={whatsappForm.access_token} onChange={e => setWhatsappForm({...whatsappForm, access_token: e.target.value})} 
                      placeholder="••••••••" className="w-full px-4 py-2.5 border rounded-lg focus:border-green-500 focus:outline-none" data-testid="input-wa-token" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Verify Token</label>
                    <input value={whatsappForm.verify_token} onChange={e => setWhatsappForm({...whatsappForm, verify_token: e.target.value})} 
                      placeholder="your_verify_token" className="w-full px-4 py-2.5 border rounded-lg focus:border-green-500 focus:outline-none" data-testid="input-wa-verify" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={saveWhatsappBusiness} disabled={saving} className="px-6 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50" data-testid="btn-save-whatsapp">
                    {saving ? '...' : 'Save & Connect'}
                  </button>
                  <button onClick={() => setShowWhatsappForm(false)} className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Cancel</button>
                </div>
              </div>
            )}
          </div>
          )}

          {/* ====== SOCIAL MEDIA API KEYS ====== */}
          {isPlatformAdmin && (
          <div className="bg-white rounded-xl shadow p-6" data-testid="social-media-config-section">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" /></svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Social Media APIs</h3>
                  <p className="text-sm text-gray-500">{language === 'hi' ? 'Facebook, Instagram, Twitter API keys जोड़ें auto-posting के लिए' : 'Add Facebook, Instagram, Twitter API keys for auto-posting'}</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${socialMediaConfigs.length > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {socialMediaConfigs.length > 0 ? `${socialMediaConfigs.length} Connected` : 'Not Configured'}
              </span>
            </div>

            {/* Existing configs */}
            {socialMediaConfigs.length > 0 && (
              <div className="space-y-2 mb-4">
                {socialMediaConfigs.map(c => (
                  <div key={c.platform} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{c.platform === 'facebook' ? '📘' : c.platform === 'instagram' ? '📸' : '🐦'}</span>
                      <span className="font-medium capitalize">{c.platform}</span>
                      {c.page_id && <span className="text-xs text-gray-400">Page: {c.page_id}</span>}
                    </div>
                    <button onClick={() => removeSocialMedia(c.platform)} className="px-3 py-1 bg-red-100 text-red-600 rounded-lg text-sm hover:bg-red-200">Remove</button>
                  </div>
                ))}
              </div>
            )}

            {!showSocialForm ? (
              <button onClick={() => setShowSocialForm(true)} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium" data-testid="btn-configure-social">
                + {language === 'hi' ? 'Social Media API जोड़ें' : 'Add Social Media API'}
              </button>
            ) : (
              <div className="space-y-4 p-5 bg-blue-50 rounded-xl mt-2 border border-blue-200" data-testid="social-form">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                  <select value={socialPlatform} onChange={e => setSocialPlatform(e.target.value)} className="w-full px-4 py-2.5 border rounded-lg focus:border-blue-500 focus:outline-none" data-testid="select-social-platform">
                    <option value="facebook">Facebook</option>
                    <option value="instagram">Instagram</option>
                    <option value="twitter">Twitter / X</option>
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API Key / App ID</label>
                    <input value={socialForm.api_key} onChange={e => setSocialForm({...socialForm, api_key: e.target.value})} 
                      className="w-full px-4 py-2.5 border rounded-lg focus:outline-none" data-testid="input-social-api-key" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API Secret</label>
                    <input type="password" value={socialForm.api_secret} onChange={e => setSocialForm({...socialForm, api_secret: e.target.value})} 
                      className="w-full px-4 py-2.5 border rounded-lg focus:outline-none" data-testid="input-social-api-secret" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Access Token</label>
                    <input type="password" value={socialForm.access_token} onChange={e => setSocialForm({...socialForm, access_token: e.target.value})} 
                      className="w-full px-4 py-2.5 border rounded-lg focus:outline-none" data-testid="input-social-access-token" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Page ID</label>
                    <input value={socialForm.page_id} onChange={e => setSocialForm({...socialForm, page_id: e.target.value})} 
                      className="w-full px-4 py-2.5 border rounded-lg focus:outline-none" data-testid="input-social-page-id" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={saveSocialMedia} disabled={saving} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50" data-testid="btn-save-social">
                    {saving ? '...' : 'Save & Connect'}
                  </button>
                  <button onClick={() => { setShowSocialForm(false); setSocialForm({ api_key: '', api_secret: '', access_token: '', page_id: '' }); }} className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Cancel</button>
                </div>
              </div>
            )}
          </div>
          )}

          {/* ====== EXISTING: Twilio ====== */}
          {isPlatformAdmin && (
          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-2xl">💬</div>
                <div>
                  <h3 className="font-semibold text-lg">WhatsApp (Twilio)</h3>
                  <p className="text-sm text-gray-500">{language === 'hi' ? 'Twilio WhatsApp Sandbox (Testing)' : 'Twilio WhatsApp Sandbox (Testing)'}</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm ${integrations?.twilio?.configured ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {integrations?.twilio?.configured ? 'Connected' : 'Not Configured'}
              </span>
            </div>
            {integrations?.twilio?.configured ? (
              <div className="space-y-3">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-gray-500">Account SID:</span><span className="ml-2 font-mono">{integrations.twilio.account_sid}</span></div>
                    <div><span className="text-gray-500">WhatsApp:</span><span className="ml-2">{integrations.twilio.whatsapp_number}</span></div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={testTwilio} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">Test</button>
                  <button onClick={removeTwilio} className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">Remove</button>
                </div>
              </div>
            ) : (
              <div>
                {!showTwilioForm ? (
                  <button onClick={() => setShowTwilioForm(true)} className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600">+ Configure Twilio</button>
                ) : (
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                    <input placeholder="Account SID" value={twilioForm.account_sid} onChange={(e) => setTwilioForm({...twilioForm, account_sid: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
                    <input placeholder="Auth Token" type="password" value={twilioForm.auth_token} onChange={(e) => setTwilioForm({...twilioForm, auth_token: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
                    <input placeholder="WhatsApp Number" value={twilioForm.whatsapp_number} onChange={(e) => setTwilioForm({...twilioForm, whatsapp_number: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
                    <div className="flex gap-2">
                      <button onClick={saveTwilio} disabled={saving} className="px-4 py-2 bg-green-500 text-white rounded-lg disabled:opacity-50">{saving ? '...' : 'Save'}</button>
                      <button onClick={() => setShowTwilioForm(false)} className="px-4 py-2 bg-gray-200 rounded-lg">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* ====== EXISTING: Razorpay ====== */}
          {isSchoolAdmin && (
          <div className="bg-white rounded-xl shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-2xl">💳</div>
                <div>
                  <h3 className="font-semibold text-lg">Razorpay</h3>
                  <p className="text-sm text-gray-500">{language === 'hi' ? 'Online fee payments स्वीकार करें' : 'Accept online fee payments'}</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm ${integrations?.razorpay?.configured ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {integrations?.razorpay?.configured ? 'Connected' : 'Not Configured'}
              </span>
            </div>
            <button
              onClick={downloadRazorpayGuide}
              className="mb-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 text-sm font-semibold transition"
              data-testid="razorpay-setup-guide-btn"
            >
              <i className="fa-solid fa-file-pdf text-blue-500"></i>
              {language === 'hi' ? '📘 Razorpay Setup Guide download करें' : '📘 Download Razorpay Setup Guide'}
            </button>
            {integrations?.razorpay?.configured ? (
              <div className="space-y-3">
                <div className="p-4 bg-gray-50 rounded-lg"><span className="text-gray-500">Key ID:</span><span className="ml-2 font-mono">{integrations.razorpay.key_id}</span></div>
                <button onClick={removeRazorpay} className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">Remove</button>
              </div>
            ) : (
              <div>
                {!showRazorpayForm ? (
                  <button onClick={() => setShowRazorpayForm(true)} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">+ Configure Razorpay</button>
                ) : (
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                    <input placeholder="Key ID" value={razorpayForm.key_id} onChange={(e) => setRazorpayForm({...razorpayForm, key_id: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
                    <input placeholder="Key Secret" type="password" value={razorpayForm.key_secret} onChange={(e) => setRazorpayForm({...razorpayForm, key_secret: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
                    <div className="flex gap-2">
                      <button onClick={saveRazorpay} disabled={saving} className="px-4 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50">{saving ? '...' : 'Save'}</button>
                      <button onClick={() => setShowRazorpayForm(false)} className="px-4 py-2 bg-gray-200 rounded-lg">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* Security Note */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <h4 className="font-medium text-amber-800 mb-2">{language === 'hi' ? '🔐 सुरक्षा नोट' : '🔐 Security Note'}</h4>
            <p className="text-sm text-amber-700">
              {isSchoolAdmin
                ? (language === 'hi' ? 'School की Gemini, SMTP और Razorpay credentials secure form में store होती हैं। पहले अपनी test key से verify करें, फिर live use करें।' : 'School Gemini, SMTP, and Razorpay credentials are stored securely. Verify with your own test keys before going live.')
                : (language === 'hi' ? 'Platform Admin के webhook, API और social credentials secure form में store होते हैं। School-owned keys अब School Admin settings/social media flow में रखी जाएँगी।' : 'Platform webhook, API, and social credentials are stored securely. School-owned keys now stay in the School Admin settings/social media flow.')}
            </p>
          </div>
        </div>
      )}


      {/* ==================== CONNECTION HISTORY TAB ==================== */}
      {activeTab === 'history' && (
        <div className="space-y-6" data-testid="connection-history-tab">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <h2 className="text-xl font-bold text-slate-800">{language === 'hi' ? 'Connection History' : 'Connection History'}</h2>
              <p className="text-sm text-gray-500">{language === 'hi' ? 'Credentials changes, connection tests, और email dispatch की history' : 'Track credential changes, connection tests, and email dispatches'}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => loadConnectionHistory(historyFilter)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 text-sm font-medium" data-testid="btn-refresh-history">
                {historyLoading ? '...' : (language === 'hi' ? 'Refresh' : 'Refresh')}
              </button>
              <button onClick={clearHistory} className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 text-sm font-medium" data-testid="btn-clear-history">
                {language === 'hi' ? 'Clear History' : 'Clear History'}
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{language === 'hi' ? 'कुल Events' : 'Total Events'}</p>
              <p className="text-2xl font-bold text-slate-800 mt-1" data-testid="stat-total-events">{connectionLogs.length}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-green-100">
              <p className="text-xs text-green-600 uppercase tracking-wide">{language === 'hi' ? 'सफल' : 'Successful'}</p>
              <p className="text-2xl font-bold text-green-700 mt-1" data-testid="stat-success-events">{connectionLogs.filter(l => l.status === 'success').length}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-red-100">
              <p className="text-xs text-red-600 uppercase tracking-wide">{language === 'hi' ? 'त्रुटियाँ' : 'Errors'}</p>
              <p className="text-2xl font-bold text-red-700 mt-1" data-testid="stat-error-events">{connectionLogs.filter(l => l.status === 'error').length}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-orange-100">
              <p className="text-xs text-orange-600 uppercase tracking-wide">{language === 'hi' ? 'Emails भेजे' : 'Emails Sent'}</p>
              <p className="text-2xl font-bold text-orange-700 mt-1" data-testid="stat-emails-sent">{emailStats.total_sent}</p>
              <p className="text-xs text-gray-400 mt-0.5">{emailStats.total_newsletters} {language === 'hi' ? 'newsletters' : 'newsletters'}</p>
            </div>
          </div>

          {/* Filter */}
          <div className="flex gap-2 flex-wrap">
            <span className="text-sm text-gray-500 py-2">{language === 'hi' ? 'Filter:' : 'Filter:'}</span>
            {[
              { label: language === 'hi' ? 'सभी' : 'All', value: '' },
              { label: 'SMTP', value: 'smtp' },
              { label: 'WhatsApp', value: 'whatsapp' },
              { label: 'Facebook', value: 'facebook' },
              { label: 'Instagram', value: 'instagram' },
              { label: 'Twitter', value: 'twitter' },
            ].map(f => (
              <button key={f.value} onClick={() => { setHistoryFilter(f.value); loadConnectionHistory(f.value); }}
                className={`px-3 py-1.5 rounded-full text-sm transition ${historyFilter === f.value ? 'bg-slate-800 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-400'}`}
                data-testid={`filter-${f.value || 'all'}`}
              >{f.label}</button>
            ))}
          </div>

          {/* Timeline */}
          {historyLoading ? (
            <div className="text-center py-12 text-gray-400">{language === 'hi' ? 'Loading...' : 'Loading...'}</div>
          ) : connectionLogs.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center shadow-sm" data-testid="history-empty">
              <div className="text-4xl mb-3">📋</div>
              <p className="text-gray-500 font-medium">{language === 'hi' ? 'अभी कोई history नहीं है' : 'No connection history yet'}</p>
              <p className="text-sm text-gray-400 mt-1">{language === 'hi' ? 'Credentials save या test करने पर यहाँ log दिखेगा' : 'Logs will appear when you save or test credentials'}</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="history-timeline">
              <div className="divide-y divide-slate-100">
                {connectionLogs.map((log, i) => {
                  const eventIcons = {
                    credential_saved: { icon: '🔑', color: 'bg-blue-100 text-blue-700' },
                    credential_removed: { icon: '🗑️', color: 'bg-red-100 text-red-700' },
                    test_connection: { icon: log.status === 'success' ? '✅' : '❌', color: log.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700' },
                    email_sent: { icon: '📧', color: log.status === 'success' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700' },
                  };
                  const ev = eventIcons[log.event_type] || { icon: '📝', color: 'bg-gray-100 text-gray-700' };
                  const serviceLabels = { smtp: 'Email SMTP', whatsapp: 'WhatsApp', social_media_facebook: 'Facebook', social_media_instagram: 'Instagram', social_media_twitter: 'Twitter' };
                  const timeStr = log.timestamp ? new Date(log.timestamp).toLocaleString('hi-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

                  return (
                    <div key={i} className="flex items-start gap-4 px-5 py-4 hover:bg-slate-50 transition" data-testid={`history-item-${i}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg shrink-0 ${ev.color}`}>
                        {ev.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-800 text-sm">{log.message}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${log.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {log.status === 'success' ? (language === 'hi' ? 'सफल' : 'Success') : (language === 'hi' ? 'त्रुटि' : 'Error')}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                          <span>{serviceLabels[log.service] || log.service}</span>
                          <span>•</span>
                          <span>{log.event_type?.replace(/_/g, ' ')}</span>
                          <span>•</span>
                          <span>{timeStr}</span>
                        </div>
                        {log.details && Object.keys(log.details).length > 0 && (
                          <div className="mt-1.5 text-xs text-gray-400 font-mono bg-slate-50 rounded px-2 py-1 inline-block">
                            {Object.entries(log.details).filter(([k]) => k !== 'newsletter_id').map(([k, v]) => `${k}: ${v}`).join(' | ')}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Team Member Modal */}
      <Modal isOpen={showTeamModal} onClose={() => { setShowTeamModal(false); setEditingMember(null); }} title={editingMember ? (language === 'hi' ? 'सदस्य एडिट करें' : 'Edit Member') : (language === 'hi' ? 'नया सदस्य जोड़ें' : 'Add New Member')}>
        <div className="space-y-4">
          {/* Photo Upload */}
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300">
              {teamForm.photo_url ? (
                <img src={teamForm.photo_url} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl text-gray-400">👤</span>
              )}
            </div>
            <div>
              <label className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg cursor-pointer hover:bg-orange-200">
                📷 {language === 'hi' ? 'फोटो अपलोड करें' : 'Upload Photo'}
                <input type="file" accept="image/*" onChange={handleTeamPhotoUpload} className="hidden" />
              </label>
              <p className="text-xs text-gray-400 mt-1">Max 1MB, Square photo best</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600">{language === 'hi' ? 'नाम (English)' : 'Name (English)'} *</label>
              <input value={teamForm.name || ''} onChange={e => setTeamForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Dr. Ramesh Kumar" className="w-full px-3 py-2 border rounded-lg mt-1" />
            </div>
            <div>
              <label className="text-sm text-gray-600">{language === 'hi' ? 'नाम (हिंदी)' : 'Name (Hindi)'}</label>
              <input value={teamForm.name_hindi || ''} onChange={e => setTeamForm(p => ({ ...p, name_hindi: e.target.value }))}
                placeholder="डॉ. रमेश कुमार" className="w-full px-3 py-2 border rounded-lg mt-1" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600">{language === 'hi' ? 'पद (English)' : 'Role (English)'} *</label>
              <select value={teamForm.role || ''} onChange={e => setTeamForm(p => ({ ...p, role: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg mt-1">
                <option value="">Select Role</option>
                <option value="Principal">Principal</option>
                <option value="Vice Principal">Vice Principal</option>
                <option value="Head Teacher">Head Teacher</option>
                <option value="Senior Teacher">Senior Teacher</option>
                <option value="Teacher">Teacher</option>
                <option value="Administrator">Administrator</option>
                <option value="Accountant">Accountant</option>
                <option value="Librarian">Librarian</option>
                <option value="Sports Coach">Sports Coach</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-600">{language === 'hi' ? 'पद (हिंदी)' : 'Role (Hindi)'}</label>
              <input value={teamForm.role_hindi || ''} onChange={e => setTeamForm(p => ({ ...p, role_hindi: e.target.value }))}
                placeholder="प्रधानाचार्य" className="w-full px-3 py-2 border rounded-lg mt-1" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600">{language === 'hi' ? 'योग्यता' : 'Qualification'}</label>
              <input value={teamForm.qualification || ''} onChange={e => setTeamForm(p => ({ ...p, qualification: e.target.value }))}
                placeholder="M.A., B.Ed." className="w-full px-3 py-2 border rounded-lg mt-1" />
            </div>
            <div>
              <label className="text-sm text-gray-600">{language === 'hi' ? 'अनुभव' : 'Experience'}</label>
              <input value={teamForm.experience || ''} onChange={e => setTeamForm(p => ({ ...p, experience: e.target.value }))}
                placeholder="15 years" className="w-full px-3 py-2 border rounded-lg mt-1" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600">{language === 'hi' ? 'फोन नंबर' : 'Phone'}</label>
              <input value={teamForm.phone || ''} onChange={e => setTeamForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="9876543210" className="w-full px-3 py-2 border rounded-lg mt-1" />
            </div>
            <div>
              <label className="text-sm text-gray-600">{language === 'hi' ? 'ईमेल' : 'Email'}</label>
              <input value={teamForm.email || ''} onChange={e => setTeamForm(p => ({ ...p, email: e.target.value }))}
                placeholder="teacher@school.com" className="w-full px-3 py-2 border rounded-lg mt-1" />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button onClick={() => { setShowTeamModal(false); setEditingMember(null); }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
              {language === 'hi' ? 'रद्द करें' : 'Cancel'}
            </button>
            <button onClick={saveTeamMember} disabled={saving} className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50">
              {saving ? '...' : (language === 'hi' ? '💾 सेव करें' : '💾 Save')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Plan Selection Modal — Simplified for Unified FREE Plan */}
      <Modal isOpen={showPlanModal} onClose={() => setShowPlanModal(false)} title={language === 'hi' ? 'आपका Plan' : 'Your Plan'}>
        <div className="space-y-4 text-center">
          <div className="w-20 h-20 mx-auto bg-gradient-to-br from-orange-500 to-green-500 rounded-full flex items-center justify-center text-4xl">
            🎓
          </div>
          <h3 className="text-2xl font-bold text-gray-800">
            {language === 'hi' ? 'Software Lifetime FREE' : 'Software Lifetime FREE'}
          </h3>
          <p className="text-gray-600">
            {language === 'hi' 
              ? 'सभी Schools एक ही unified plan पर हैं — कोई Basic/Standard/Premium tier नहीं।' 
              : 'All schools are on one unified plan — no more Basic/Standard/Premium tiers.'}
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-700">
            ✓ {language === 'hi' ? 'सभी ERP Modules Free' : 'All ERP Modules Free'}<br/>
            ✓ {language === 'hi' ? '31 AI Features (आपकी Gemini Key पर)' : '31 AI Features (on your Gemini Key)'}<br/>
            ✓ {language === 'hi' ? 'One-time ₹4999 + ₹999/साल maintenance' : 'One-time ₹4999 + ₹999/year maintenance'}
          </div>
          <button 
            onClick={() => setShowPlanModal(false)}
            className="w-full px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-semibold"
          >
            {language === 'hi' ? 'समझ गया' : 'Got it'}
          </button>
        </div>
      </Modal>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// ============== NEW MODULES PAGES ==============

// Transport Management Page

export default SettingsPage;
